/**
 * Trait-driven fact-row editor (#133).
 *
 * Replaces the Walking-Skeleton `population` / `government` hardcode
 * with a generic editor that reads `entity.traits[]`, looks up each
 * trait in the merged pack bundle, and renders one row per declared
 * predicate. The row's input shape is driven by the predicate's
 * `value_kind`; cardinality `'many'` surfaces a list with add/remove
 * controls.
 *
 * The validity range, fact-status chips, and per-fact history panel
 * are shared across every value-kind via `viewFactChips`,
 * `viewValidityRow`, and `viewHistory` — all defined here so the
 * editor stays in one module.
 *
 * Hard rules enforced here:
 *   - No `VisibleFact` / `VisibleOp` construction (chokepoint lives
 *     in `packages/projection`).
 *   - All writes flow through the existing `edit-fact` /
 *     `soft-delete-fact` Msgs, which append ops via the SyncClient.
 *   - Every UI string flows through `t()`; per-predicate labels are
 *     `predicate.<id>.label` and `entity.set_<id>` placeholders, so
 *     the i18n catalog sweep picks them up.
 */
import {
  calendarBag,
  formatDisplayDate,
  loadBundledCalendar,
  toDisplayDate,
  type Calendar,
  type CycleDefinition,
} from '@dl/calendar';
import {
  BUILTIN_NAME_PREDICATE,
  DEFAULT_IMAGE_ACCEPT,
  PredicateRegistry,
  UnitRegistry,
  allowedRefRoots,
  commonUnitRegistry,
  containmentContext,
  DEFAULT_CONTAINMENT_CONTEXT,
  isHexColor,
  loadBundledPack,
  hasEmptyUniquenessKey,
  hasObjectUniquenessKey,
  loadCoreInto,
  mergeUnitRegistry,
  OFFICIAL_CORE_PACK_ID,
  decimalToString,
  fromCanonicalValue,
  playerAuthorityPredicateIds,
  readNumberValue,
  resolveDisplayUnit,
  registerBuiltins,
  registerPack,
  resolveBaseSegmentAt,
  toCanonicalValue,
  validateValueWithDeclaration,
  type DimensionDefinition,
  type EntityRoot,
  type EntityState,
  type FacetTemplate,
  type FactState,
  type FactValidity,
  type LocalizedString,
  type PredicateDeclaration,
  type TraitDeclaration,
  type TupleFieldDeclaration,
  type UnitDefinition,
  type ValueKind,
} from '@dl/shared';
import {
  badge,
  button as uiButton,
  type CalendarCursor,
  type CalendarDatePickerMessages,
  type CalendarDatePickerOverlay,
  type CalendarGranularity,
  colorPicker,
  dateField,
  type DateFieldMessages,
  modal,
  provenanceBadge,
  revealChip,
  statusPill,
  textInput,
  uiIcon,
  validityEditor,
} from '@dl/ui';
import {
  derived,
  div,
  each,
  h2,
  img,
  input,
  label,
  li,
  mountApp,
  mountable,
  ol,
  option,
  select,
  show,
  span,
  text,
  textarea,
  type Mountable,
  type Renderable,
  type Send,
  type Signal,
} from '@llui/dom';
import { markdownEditor } from '@llui/markdown-editor';

import { campaignAccentSwatches, colorPickerMessages } from '../color-swatches.js';
import { t } from '../i18n.js';


// Mount adapter for @llui/markdown-editor. The fact-row uses eager builders
// (real DOM nodes), but the editor's `mountApp` needs its host ATTACHED to the
// document (Lexical), and a synchronous mount during render throws. So a thin
// custom element bridges the two: `connectedCallback` mounts the Lexical editor
// into itself on attach; `disconnectedCallback` disposes it. It emits
// `md-change` (latest markdown, debounced) so the row tracks dirtiness +
// commits on blur. This is a mount lifecycle adapter — the editing UI itself is
// entirely @llui/markdown-editor.
const MD_EDITOR_TAG = 'dl-llui-md-editor';
if (typeof customElements !== 'undefined' && customElements.get(MD_EDITOR_TAG) === undefined) {
  customElements.define(
    MD_EDITOR_TAG,
    class extends HTMLElement {
      private handle: { dispose?: () => void } | null = null;
      // Latest markdown the inner editor holds — seeded from the `value`
      // attribute and updated on every `onChange`. Exposed as a `.value`
      // getter so the current markdown is programmatically readable (the
      // inner Lexical contenteditable has no stable value DOM contract).
      private current = '';
      get value(): string {
        return this.current;
      }
      connectedCallback(): void {
        if (this.handle) return;
        const mode = this.getAttribute('mode') ?? 'full';
        this.current = this.getAttribute('value') ?? '';
        this.handle = mountApp(
          this,
          markdownEditor({
            defaultValue: this.getAttribute('value') ?? '',
            readonly: mode === 'none',
            toolbar: mode === 'full',
            placeholder: this.getAttribute('placeholder') ?? '',
            onChange: (md: string): void => {
              this.current = md;
              this.dispatchEvent(new CustomEvent('md-change', { detail: { value: md } }));
            },
          }),
        ) as unknown as { dispose?: () => void };
      }
      disconnectedCallback(): void {
        this.handle?.dispose?.();
        this.handle = null;
      }
    },
  );
}


import {
  aiDraftErrorAtKey,
  aiDraftPendingAtKey,
  assetUrlAtPredicate,
  authorAtPredicate,
  entityAtId,
  factAtPredicate,
  factDirtyAtKey,
  factHintDismissedOf,
  factRowKey,
  factSavedAtKey,
  factSavingAtKey,
  focusedFactKeyOf,
  inlineErrorAtKey,
  inputBufferAtKey,
  pickActiveCalendarId,
  traitsAtId,
  type FactRowStateShape,
} from './fact-row-state.js';
import { getHomebrewDefs, homebrewEpoch } from './homebrew-defs.js';
import { buildSegmentStrip, formatSegmentLabel, type SegmentBar } from './segment-strip.js';
import { traitLabel } from './trait-attach-view.js';
import { campaignUnitDefaults, userUnitOverrides } from './unit-display-prefs.js';

// ────────────────────────────────────────────────────────────────────
// Pack-registry bootstrap.
//
// The client bundles `official-core` (ADR-0004 §3, #183 — the former
// `core.json` content reborn as the first official pack with opaque
// minted ids) via the `@dl/shared` pack loader. A later slice will
// derive the subscribed pack set from campaign config; for Phase 1 the
// GM client subscribes `official-core` unconditionally so the content
// vocabulary (`Population`, `Inhabited`, …) resolves as before.
//
// `loadGmRegistry()` is intentionally synchronous — the loader is
// already sync — so call sites inside reactive accessors don't need
// to suspend.
// ────────────────────────────────────────────────────────────────────

let _registry: PredicateRegistry | null = null;
let _registryEpoch = -1;
export function loadGmRegistry(): PredicateRegistry {
  const epoch = homebrewEpoch();
  if (_registry && _registryEpoch === epoch) return _registry;
  const r = new PredicateRegistry();
  loadCoreInto(r);
  // The one built-in system predicate (`name`) is present in every
  // campaign independent of pack subscriptions (ADR-0004 §2/§3). Register
  // it so the title row resolves from the code constant, not a token.
  registerBuiltins(r);
  // Subscribe the official starter pack (#183) — the former core.json
  // content vocabulary, now keyed by opaque minted ids.
  registerPack(r, loadBundledPack(OFFICIAL_CORE_PACK_ID));
  // Overlay the campaign-local homebrew Pack's predicates on top of the
  // official pack (ADR-0001). Opaque ids never collide; a homebrew id
  // simply adds (or, with a deliberate matching id, overwrites).
  for (const decl of getHomebrewDefs().predicates) r.add(decl);
  _registry = r;
  _registryEpoch = epoch;
  return r;
}

/** Test seam — drop the memoised registry so a test catalog can be re-installed. */
export function _resetGmRegistry(): void {
  _registry = null;
  _registryEpoch = -1;
}

// ────────────────────────────────────────────────────────────────────
// Trait → predicate resolution.
//
// `entity.traits[]` is the source of truth; we walk it in order,
// expand to the trait's `predicates[]`, and deduplicate (a predicate
// may surface in multiple traits — `government` lives in both
// `inhabited` and `governed`). We also strip the implicit `name`
// predicate because every entity is treated as named — the name row
// renders unconditionally outside the trait loop.
// ────────────────────────────────────────────────────────────────────

const ALWAYS_RENDER_FIRST: ReadonlySet<string> = new Set(['name']);

/**
 * Per `03-information-model.md §5` traits are declarations of which
 * predicates apply to an entity root; the `Pack.traits` table carries
 * the mapping. We don't have the traits table on the registry, so we
 * walk every loaded pack to find trait declarations for an entity's
 * trait set.
 *
 * Returns the deduplicated, in-order predicate ids that the entity
 * surfaces (minus the implicit ones).
 */
export function predicateIdsForTraits(
  traits: readonly string[],
  registry: PredicateRegistry,
  traitToPredicates: ReadonlyMap<string, readonly string[]>,
): readonly string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const traitId of traits) {
    const predicates = traitToPredicates.get(traitId);
    if (!predicates) continue;
    for (const pid of predicates) {
      if (ALWAYS_RENDER_FIRST.has(pid)) continue;
      if (seen.has(pid)) continue;
      // Skip predicates that aren't in the registry — corrupt /
      // pre-decl ops shouldn't crash the renderer.
      if (!registry.lookup(pid)) continue;
      seen.add(pid);
      out.push(pid);
    }
  }
  return out;
}

/**
 * Which attached trait a predicate row came from.
 *
 * Mirrors `predicateIdsForTraits`' first-wins order, so a predicate declared by
 * two attached traits reports the same owner the row list used. Returns null
 * for `name` (built in, owned by no trait) and for anything unattached.
 */
export function owningTraitFor(
  predicateId: string,
  traits: readonly string[],
  traitToPredicates: ReadonlyMap<string, readonly string[]>,
): string | null {
  if (ALWAYS_RENDER_FIRST.has(predicateId)) return null;
  for (const traitId of traits) {
    const predicates = traitToPredicates.get(traitId);
    if (predicates?.includes(predicateId)) return traitId;
  }
  return null;
}

// ────────────────────────────────────────────────────────────────────
// Trait → predicate index, derived from loaded packs.
//
// We re-derive at module load (cheap — packs are bundled JSON) so
// the index is in sync with `loadGmRegistry()`. A Phase 2 multi-pack
// loader path will feed a different builder.
// ────────────────────────────────────────────────────────────────────

let _traitIndex: ReadonlyMap<string, readonly string[]> | null = null;
let _traitIndexEpoch = -1;
function buildTraitIndex(): ReadonlyMap<string, readonly string[]> {
  const index = new Map<string, readonly string[]>();
  for (const id of [OFFICIAL_CORE_PACK_ID]) {
    const pack = loadBundledPack(id);
    for (const trait of pack.traits) {
      // Last loaded wins for the same trait id — Phase 2's pack
      // collision policy will refine this; for now `official-core` is
      // the only contributor of the traits we render in Phase 1.
      index.set(trait.id, trait.predicates);
    }
  }
  // Campaign-local homebrew traits (ADR-0001) overlay core — a homebrew
  // trait may redefine a core trait's predicate set (last writer wins).
  for (const trait of getHomebrewDefs().traits) {
    index.set(trait.id, trait.predicates);
  }
  return index;
}

export function getTraitIndex(): ReadonlyMap<string, readonly string[]> {
  const epoch = homebrewEpoch();
  if (_traitIndex && _traitIndexEpoch === epoch) return _traitIndex;
  _traitIndex = buildTraitIndex();
  _traitIndexEpoch = epoch;
  return _traitIndex;
}

/** Test seam — drop the memoised trait index. */
export function _resetTraitIndex(): void {
  _traitIndex = null;
  _traitIndexEpoch = -1;
}

/**
 * Gather the bulk-reveal facet templates contributed by a set of traits
 * (`Trait.default_facets`, `03a §3/§11`) — the entity's own traits. Walks the
 * loaded core pack + the homebrew overlay; dedupes by facet id (first
 * declaration wins). Feeds `entityFacets()` for the §7.10 reveal UI.
 */
export function facetTemplatesForTraits(traitIds: readonly string[]): FacetTemplate[] {
  const wanted = new Set(traitIds);
  const byId = new Map<string, FacetTemplate>();
  const consume = (traits: readonly TraitDeclaration[]): void => {
    for (const trait of traits) {
      if (!wanted.has(trait.id)) continue;
      for (const facet of trait.default_facets ?? []) {
        if (!byId.has(facet.id)) byId.set(facet.id, facet);
      }
    }
  };
  for (const id of [OFFICIAL_CORE_PACK_ID]) consume(loadBundledPack(id).traits);
  consume(getHomebrewDefs().traits);
  return [...byId.values()];
}

/** A predicate's `default_facet` (the facet its facts join), or undefined. */
export function predicateDefaultFacet(predicateId: string): string | undefined {
  return loadGmRegistry().lookup(predicateId)?.default_facet;
}

// ────────────────────────────────────────────────────────────────────
// Editor host shape.
//
// `viewEntityFactRows` is the renderer the entity page calls. It is
// generic over the host app's `Model` and `Msg` so this module does
// not need to import `app.ts` (avoiding a circular import). The host
// passes an accessor that resolves an entity from the model and
// message constructors for the edit-fact / soft-delete-fact /
// set-fact-validity / rollback-fact / invalid-fact
// channel. See `apps/client/README.md` "View dispatcher patterns" —
// every accessor parameter is rebound to a same-module const so the
// llui v2c walker can resolve it statically.
// ────────────────────────────────────────────────────────────────────

/**
 * Host shape: Msg constructors + the non-reactive `activeCalendar`.
 *
 * Reactive state reads (`factAt`, `dirtyAt`, `savedAtMs`, …) used to
 * live here as `(s, e, p) => …` arrow methods. The llui v0.5.7
 * compile-time path analyzer treats arg0 of a `host.method(s)`
 * call as opaque (`detectOpaqueStateFlow` only tracks bare-Identifier
 * callees), so the editor's reactive accessors were silently invisible
 * to `__prefixes` emission — keystrokes never re-evaluated bindings.
 * Those readers now route through `./fact-row-state.ts`'s
 * function declarations, which the analyzer follows. Msg constructors
 * stay here because they're event-handler callbacks, never on a
 * reactive path. See `apps/client/e2e/09-create-edit-navigate.spec.ts §9.4`.
 */
export interface FactRowHost<Model, Msg> {
  /** Active calendar — bound to the model's active-calendar id. Non-reactive (only read inside h.scope/h.sample contexts). */
  activeCalendar: (s: Model) => Calendar;
  /**
   * Current Campaign date (session cursor) — the in-world time the GM
   * is working in, or null when unset. Seeds a freshly un-open-ended
   * validity bound. Non-reactive (read inside h.scope/h.sample).
   */
  campaignCursor: (s: Model) => number | null;
  /**
   * The EPHEMERAL in-world browse cursor (as-of scrubber — ADR-0005 §3,
   * #192), or null when browsing "now". When non-null the GM is browsing the
   * world AS OF this day: the row resolves its temporal value at this `t` and
   * "new at now" authoring is disabled (existing-segment edits stay allowed).
   */
  browseCursor: (s: Model) => number | null;
  /**
   * The single open calendar-grid date picker (`Model.datePicker`), or null
   * when none is open. The grid's transient view-state (cursor/overlay) is a
   * single app-wide slice because only one grid is interactive at a time;
   * `id` namespaces which field owns it (`fact:<entity>:<predicate>:day`).
   */
  datePicker: (
    s: Model,
  ) => { id: string; cursor: CalendarCursor; overlay: CalendarDatePickerOverlay } | null;
  /** Localized message catalog for the grid + the dateField shell. */
  datePickerMessages: () => Partial<CalendarDatePickerMessages & DateFieldMessages>;
  /** Open the grid for `id`, seeded at the value-derived `cursor`. */
  openDatePicker: (id: string, cursor: CalendarCursor) => Msg;
  /** The user navigated the open grid. */
  navigateDatePicker: (cursor: CalendarCursor) => Msg;
  /** The user toggled the inline month/era overlay. */
  overlayDatePicker: (overlay: CalendarDatePickerOverlay) => Msg;
  /** Dismiss the open grid. */
  closeDatePicker: () => Msg;
  /** Message constructors. */
  editFact: (entityId: string, predicate: string, value: unknown) => Msg;
  softDeleteFact: (entityId: string, predicate: string) => Msg;
  invalidFact: (entityId: string, predicate: string, error: string) => Msg;
  rollbackFact: (entityId: string, predicate: string, toIndex: number) => Msg;
  /** Open the per-fact History dialog for this (entity, predicate). */
  openHistory: (entityId: string, predicate: string) => Msg;
  /** Dismiss the History dialog. */
  closeHistory: () => Msg;
  setFactValidity: (
    entityId: string,
    predicate: string,
    validity: FactValidity | null,
  ) => Msg;
  /**
   * Valid-time succession (ADR-0005 §3 / #188) — "this changed as of day X".
   * Closes the predecessor segment and opens a new `seg_…` successor. The
   * destructive predecessor-close is the action the GM confirms in the UI;
   * the substrate applies it as two ordinary ops (no silent merge).
   */
  succeedFact: (entityId: string, predicate: string, atDay: number, value: unknown) => Msg;
  /**
   * #156 — mark the row dirty (called from `onInput` on every
   * keystroke). The reducer bumps the per-key epoch and folds the
   * optional `buffer` string into `Model.inputBuffer[key]` — the
   * canonical llui dirty-buffer accessor pattern (2026-05-27). Pass
   * the live DOM value as `buffer` so the editor's reactive `value:`
   * accessor reads back what the user typed across subsequent
   * commits.
   */
  markDirty: (entityId: string, predicate: string, buffer?: string) => Msg;
  focusFact: (entityId: string, predicate: string) => Msg;
  blurFact: (entityId: string, predicate: string) => Msg;
  dismissHint: () => Msg;
  /**
   * LLM author mode (Phase B) — ✨ "Draft with AI". Asks the worker to
   * draft prose for this (entity, predicate); the result lands as a
   * draft-status fact in the Draft Inbox. Only rendered for prose
   * (`rich_text`) predicates (see `kindSupportsAiDraft`).
   */
  draftWithAi: (entityId: string, predicate: string) => Msg;
  /**
   * LLM author mode (Phase D) — ✨ "Generate image". Generates a draft
   * image for an asset predicate (portrait / hero_image); the draft asset
   * lands in the Draft Inbox. Works on empty rows (first portrait) and
   * filled rows (regenerate). `instruction` is the optional GM direction
   * to steer the art (style, framing, mood).
   */
  generateImageFor: (entityId: string, predicate: string, instruction?: string) => Msg;
  /**
   * Inline draft actions (#12) — ratify a draft fact to canon / reject it.
   * Rendered on a fact row whose current status is `draft`, so the GM can
   * act on it in the Traits tab without opening the Draft Inbox. Reuse the
   * same Msgs the inbox dispatches.
   */
  ratifyDraft: (entityId: string, predicate: string) => Msg;
  rejectDraft: (entityId: string, predicate: string) => Msg;
  /**
   * #251 §14.2 — clear the GM-only `unreviewed_bulk` marker on an auto-promoted
   * canon fact (the one-click "review-pass"). An ordinary GM-authored
   * `fact.edit` dropping `review_state` (never a status change).
   */
  clearReviewState: (entityId: string, predicate: string) => Msg;
  /**
   * Per-attribute reveal (Phase 2 / 30 §7.7) — open the Viewer-Matrix
   * popover for this fact so the GM can pick who sees it. The popover
   * renders at the app level (it needs the campaign Viewer roster); the row
   * only requests it via this Msg. Reveals are write-once and always
   * one-click-safe per fact (§7.10.3), so no confirm step here.
   */
  openRevealMenu: (entityId: string, predicate: string) => Msg;
  /**
   * Real image/file upload (#8/#9). Uploads the binary to the campaign
   * asset store (the existing `POST /v1/campaigns/:cid/assets` endpoint)
   * and resolves to the new asset id + its signed content URL. Rejects on
   * failure (size/format/network) — the caller surfaces the message. The
   * host owns the fetch so this module stays free of app-level wiring.
   */
  uploadAsset: (file: Blob) => Promise<{ id: string; url: string }>;
}

/**
 * Decode a `real_datetime_range` fact value into the pair the two
 * `<input type=datetime-local>` controls bind to. Same-module function
 * declaration so the cross-file walker resolves reactive accessors
 * that delegate here (the closure-arrow shape `(s) => readPair(s)`
 * opacifies the analyzer because the arrow closes over `entityId` /
 * `predicate` function parameters).
 */
const calendarCache = new Map<string, Calendar>();
/**
 * Resolve a calendar by id with a module-scoped cache. Same-module
 * function declaration so the cross-file walker can resolve reactive
 * accessors that delegate here — `host.activeCalendar(s)` was the
 * opaque-state-flow anti-pattern.
 */
function loadActiveCalendar(calendarId: string): Calendar {
  let cached = calendarCache.get(calendarId);
  if (cached !== undefined) return cached;
  cached = loadBundledCalendar(calendarId);
  calendarCache.set(calendarId, cached);
  return cached;
}

/**
 * Decode a multi-value fact value into the array the per-row inputs
 * bind to. Same-module function declaration so the cross-file walker
 * can resolve reactive accessors that delegate here.
 */
function readMultiArr(fact: FactState | undefined): unknown[] {
  const v = currentValue(fact);
  return Array.isArray(v) ? v : [];
}

function readDatetimeRangePair(
  fact: FactState | undefined,
): { from: string; to: string } {
  const v = currentValue(fact);
  if (
    typeof v === 'object' &&
    v !== null &&
    'from' in (v as Record<string, unknown>) &&
    'to' in (v as Record<string, unknown>)
  ) {
    const obj = v as { from: unknown; to: unknown };
    return {
      from: typeof obj.from === 'string' ? isoToLocalDatetime(obj.from) : '',
      to: typeof obj.to === 'string' ? isoToLocalDatetime(obj.to) : '',
    };
  }
  return { from: '', to: '' };
}

function currentValue(fact: FactState | undefined): unknown {
  if (!fact || fact.soft_deleted) return undefined;
  return fact.versions[fact.versions.length - 1]?.value;
}

/**
 * True when the `(entityId, predicate)` fact has a current value — i.e. it
 * has been written and not soft-deleted. Bare-identifier function so the
 * reactive reveal-chip accessor in `viewFactChips` stays traceable by the
 * llui dep analyzer (it reads `s.entities` through `factAtPredicate`).
 */
function factHasValue<Model extends FactRowStateShape>(
  s: Model,
  entityId: string,
  predicate: string,
): boolean {
  return currentValue(factAtPredicate(s, entityId, predicate)) !== undefined;
}

// ─── ref-kind (relationship) helpers ───────────────────────────────
//
// A `ref` predicate's value is the target entity's id; the GM authors
// it by picking an existing entity (filtered to the predicate's
// allowed roots, per `allowedRefRoots`). These bare-Identifier helpers
// read `s` directly so the llui walker tracks `s.entities`.

/** Display name for an entity (its `name` fact's latest value, else id). */
function refEntityName(entity: EntityState): string {
  const fact = entity.facts.name;
  const latest = fact?.versions[fact.versions.length - 1];
  const v = latest?.value;
  return v === undefined || v === null || String(v).trim() === '' ? entity.id : String(v);
}

interface RefOption {
  id: string;
  name: string;
}

/**
 * Candidate target entities for a ref predicate, plus the currently-
 * selected target (so the <select> always reflects the stored value
 * even if its root no longer matches the allowed set). Filtered to the
 * predicate's allowed roots (empty = any root), self excluded, soft-
 * deleted excluded; sorted by name.
 */
function refOptions<S extends FactRowStateShape>(
  s: S,
  entityId: string,
  predicate: string,
  roots: readonly EntityRoot[],
): RefOption[] {
  const allow = new Set<string>(roots);
  const current = currentValue(s.entities[entityId]?.facts[predicate]);
  const currentId = typeof current === 'string' ? current : null;
  const out: RefOption[] = [];
  for (const [id, entity] of Object.entries(s.entities)) {
    if (id === entityId) continue;
    if (entity.soft_deleted) continue;
    if (allow.size > 0 && !allow.has(entity.root)) {
      // Not an allowed root — include only if it's the current value
      // so the select can still show what's stored.
      if (id !== currentId) continue;
    }
    out.push({ id, name: refEntityName(entity) });
  }
  // Ensure the current target is present even if soft-deleted / missing
  // from the iteration above.
  if (currentId && !out.some((o) => o.id === currentId)) {
    const cur = s.entities[currentId];
    out.push({ id: currentId, name: cur ? refEntityName(cur) : currentId });
  }
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

// ────────────────────────────────────────────────────────────────────
// Predicate-driven label helpers.
//
// Per ticket: row label = `predicate.<id>.label`; placeholder =
// `entity.set_<id>`. We fall back to a humanised version of the id
// when no catalog entry exists so the UI is never blank.
// ────────────────────────────────────────────────────────────────────

export function predicateLabel(predicateId: string): string {
  // ADR-0004 §1: a predicate declaration carries a mandatory display
  // `label` (the GM-typed name). Prefer it — the raw opaque id is never
  // shown. Bundled `core` / built-in predicates may instead supply their
  // label via the i18n catalog, so fall through to that.
  const declared = resolveLocalizedString(loadGmRegistry().lookup(predicateId)?.label);
  if (declared) return declared;
  const key = `predicate.${predicateId}.label`;
  const v = t(key);
  if (v !== key) return v;
  // Fall back to the legacy `entity.<id>` family (so existing keys
  // like `entity.name` / `entity.population` keep working).
  const legacy = t(`entity.${predicateId}`);
  if (legacy !== `entity.${predicateId}`) return legacy;
  // Last-resort humaniser — keeps the row addressable even when the
  // catalog falls behind a freshly-added predicate.
  return predicateId.replace(/_/g, ' ');
}

export function predicatePlaceholder(predicateId: string): string {
  // Bundled predicates carry a bespoke `entity.set_<id>` placeholder. ADR-0004
  // opaque-id predicates (pack/homebrew) never do — `t()` returns the key
  // verbatim on a miss, which used to leak the raw `entity.set_pred_<uuid>`
  // key into empty text/rich_text fields. Fall back to a label-derived hint.
  const key = `entity.set_${predicateId}`;
  const v = t(key);
  if (v !== key) return v;
  return t('entity.facts.set_value', { label: predicateLabel(predicateId) });
}

/**
 * Resolve a user-authored `LocalizedString` (pack/homebrew boolean labels,
 * tuple field labels, etc.) to a display string. These are NOT i18n catalog
 * keys — they are author-supplied content — so they resolve through the
 * locale chain (preferred locale → 'en' → first key), not `t()`.
 */
function resolveLocalizedString(value: LocalizedString | undefined): string {
  if (value === undefined) return '';
  if (typeof value === 'string') return value;
  const locale = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
  const short = locale.split('-')[0] ?? 'en';
  return value[locale] ?? value[short] ?? value['en'] ?? Object.values(value)[0] ?? '';
}

// Campaign-merged unit registry (ADR-0002). The common dimensions/units
// plus the campaign-local definitions folded from the op log's `__pack`
// meta-ops (`getHomebrewDefs().dimensions / .units`). Memoised on the
// homebrew epoch so a `pack.define_unit` op surfaces in the picker on the
// next fold without rebuilding the registry on every render. Falls back to
// the common registry when there are no campaign units.
let _unitRegistry: UnitRegistry | null = null;
let _unitRegistryEpoch = -1;
export function campaignUnitRegistry(): UnitRegistry {
  const epoch = homebrewEpoch();
  if (_unitRegistry && _unitRegistryEpoch === epoch) return _unitRegistry;
  const defs = getHomebrewDefs();
  if (defs.dimensions.length === 0 && defs.units.length === 0) {
    // No campaign units — the common registry is sufficient (and cheaper).
    const reg = commonUnitRegistry();
    _unitRegistry = reg;
    _unitRegistryEpoch = epoch;
    return reg;
  }
  const dimensions: Record<string, DimensionDefinition> = {};
  for (const d of defs.dimensions) dimensions[d.id] = d;
  const units: Record<string, UnitDefinition> = {};
  for (const u of defs.units) units[u.id] = u;
  const reg = mergeUnitRegistry({
    dimensions,
    units,
    retiredDimensions: defs.retiredDimensions,
    retiredUnits: defs.retiredUnits,
  });
  _unitRegistry = reg;
  _unitRegistryEpoch = epoch;
  return reg;
}

/** Test seam — drop the memoised unit registry. */
export function _resetUnitRegistry(): void {
  _unitRegistry = null;
  _unitRegistryEpoch = -1;
}

/**
 * Units offered for a `number` predicate, scoped by its declared
 * `dimension`. Resolves against the campaign-merged unit registry
 * (common units + campaign-local definitions, ADR-0002); falls back to
 * the common set when the campaign declares none. Empty when the
 * predicate declares no dimension.
 */
function unitsForPredicate(decl: PredicateDeclaration): UnitDefinition[] {
  if (!decl.dimension) return [];
  return campaignUnitRegistry().unitsForDimension(decl.dimension);
}

/**
 * Format a `number` fact value for READ-ONLY display (ADR-0007 + 08-units.md
 * §5 display preferences, #249). A bare number stringifies; a dimensioned
 * `{ canonical_value, unit_id }` is rendered in the **preferred display unit**
 * — resolved per §5 (user override → campaign default → the fact's provenance
 * unit) — and suffixed with that unit's symbol (e.g. a fact authored in km
 * renders as `155.34 mi` when length resolves to miles). The stored
 * `canonical_value` is authoritative and untouched; only the rendered unit
 * converts (`fromCanonicalValue` IS the canonical→unit projection, exact for
 * terminating units). Returns `null` when the value isn't a number value, or
 * the provenance unit isn't in the campaign registry — callers fall back to
 * their own representation. The single display chokepoint for number+unit,
 * reused by the list summary, overview, facts tab, and as-of strip.
 */
export function formatNumberValueForDisplay(value: unknown): string | null {
  const nv = readNumberValue(value);
  if (!nv) return null;
  if (nv.kind === 'plain') return String(nv.value);
  const registry = campaignUnitRegistry();
  // Resolve the unit to render in (§5 order); falls back to the provenance
  // unit when no preference applies, and is null only if provenance is unknown.
  const unit = resolveDisplayUnit(nv.unit_id, registry, {
    user: userUnitOverrides(),
    campaign: campaignUnitDefaults(),
  });
  if (!unit) return null;
  const display = decimalToString(fromCanonicalValue(nv.canonical_value, unit));
  return `${display} ${resolveLocalizedString(unit.symbol)}`;
}

// ────────────────────────────────────────────────────────────────────
// #156 — first-focus hint dismissal (localStorage-backed).
//
// The hint surfaces below the first fact input the GM focuses in a
// given browser. Once dismissed (or after the first auto-commit), the
// flag persists in `localStorage` so the hint never re-surfaces.
//
// Keyed deliberately as `dl.factEditHintDismissed` per the ticket spec
// so a future onboarding redesign can clear the flag with a known key.
// ────────────────────────────────────────────────────────────────────

const FACT_HINT_KEY = 'dl.factEditHintDismissed';

export function isFactHintDismissed(): boolean {
  if (typeof localStorage === 'undefined') return true;
  try {
    return localStorage.getItem(FACT_HINT_KEY) === '1';
  } catch {
    // localStorage can throw on Safari private-mode + quota errors.
    // Treat as "dismissed" to avoid an infinite hint loop.
    return true;
  }
}

export function dismissFactHint(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(FACT_HINT_KEY, '1');
  } catch {
    // Silent: dismissal is a UX nicety; failure to persist just means
    // the hint surfaces again on the next page load. No correctness
    // impact.
  }
}

export function _resetFactHintForTest(): void {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(FACT_HINT_KEY);
    } catch {
      /* see isFactHintDismissed */
    }
  }
}

/**
 * Peek a trait-row's key signal (`<predicateId>#<homebrewEpoch>`) and return the
 * predicate id. The keyed `each` only invokes a row's `render` at (re)mount — the
 * key changes (and the row remounts) whenever the predicate set or homebrew epoch
 * changes — so a one-shot peek is correct: the value never changes for a live row.
 * Wrapping the `.peek()` in a bare-Identifier helper keeps it off any reactive
 * path the llui analyzer would try to trace.
 */
function peekRowPredicate(rowKeySig: Signal<string>): string {
  const key = rowKeySig.peek();
  const hash = key.lastIndexOf('#');
  return hash === -1 ? key : key.slice(0, hash);
}

// ────────────────────────────────────────────────────────────────────
// viewEntityFactRows — the main entry.
//
// Walks the entity's traits, renders `name` first (always), then one
// row per trait predicate. Each row uses a value-kind-aware input.
// ────────────────────────────────────────────────────────────────────

export function viewEntityFactRows<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  entityId: string,
  host: FactRowHost<Model, Msg>,
): Renderable {
  // `registry` here is only used for the always-present `name` row;
  // the trait-driven rows read the registry + trait index FRESH inside
  // the h.scope below so a homebrew trait attached after mount surfaces
  // its predicate rows (see the scope render comment, #73).
  const registry = loadGmRegistry();

  // The `name` predicate is rendered unconditionally as the first
  // row; every entity has a name. We look it up in the registry to
  // honour the same value-kind dispatch as the rest of the editor
  // (which keeps the input shape consistent if a pack ever overrides
  // `name` to e.g. `rich_text`).
  const nameDecl =
    registry.lookup('name') ??
    // Fallback to the built-in declaration so the row still renders even
    // if the registry was constructed without `registerBuiltins`
    // (ADR-0004 §2 — `name` is a code constant, present in every campaign).
    BUILTIN_NAME_PREDICATE;

  const rows: Mountable[] = [];
  rows.push(viewFactRowFor(state, send, nameDecl, entityId, host));

  // Trait-driven rows. The trait list is part of the entity create
  // payload and only changes when the GM toggles a trait (Phase 2
  // surface). We therefore lift the predicate list at render time
  // rather than re-deriving it on every fact mutation; the entity-page
  // subtree remounts when the entity id changes. We use a keyed `each`
  // over a single-element `[key]` array (the 0.5.x scope-remount idiom)
  // so the row list rebuilds when traits change.
  //
  // Reactive accessors call `traitsAtId` directly (a same-module
  // bare-Identifier function declaration in fact-row-state.ts) rather
  // than a local `(s) => …` arrow — closure-captured parameters
  // (`entityId`) opacify the cross-file walker.
  rows.push(
    div(
      { class: 'fact-trait-rows', 'data-testid': 'fact-trait-rows' },
      [
        each(
          // One keyed row PER trait predicate. The key is `<predicateId>#<epoch>`:
          // the predicate id remounts the row when a trait is added / removed /
          // renamed, and the homebrew epoch (bumped on every `setHomebrewDefs`)
          // remounts it when a predicate's SHAPE changes (value-kind / cardinality
          // / enum_values) without an id change. Both kinds of homebrew edit (#73 /
          // ADR-0001) therefore rebuild the affected rows against the live registry.
          //
          // Per-predicate keying (rather than a single synthetic "row" carrying the
          // whole predicate set) is load-bearing: a keyed `each` probes its row
          // template ONCE, from the FIRST row it builds, to decide whether rows read
          // component state (`templateReadsState`). A single-element each is EMPTY
          // before any trait is attached, so that first (empty) row reads no state —
          // the probe latches `false` and EVERY later row (the real fact-rows) is
          // then frozen out of state-driven updates (`data-dirty` / Save-button
          // `disabled` / status never refresh). One row per predicate makes the
          // first built row a real, state-reading fact-row, so the probe latches
          // `true` and rows stay reactive.
          state.map((s) => {
            const epoch = homebrewEpoch();
            return predicateIdsForTraits(
              traitsAtId(s, entityId),
              loadGmRegistry(),
              getTraitIndex(),
            ).map((pid) => `${pid}#${epoch}`);
          }),
          {
            key: (k) => k,
            render: (rowKeySig: Signal<string>): Renderable => {
              // Resolve the registry + trait index FRESH (not mount-time consts): a
              // campaign-local homebrew trait lands in the registry overlay AFTER
              // first mount; this row body runs on (re)mount once its key appears.
              const pid = peekRowPredicate(rowKeySig);
              const decl = loadGmRegistry().lookup(pid);
              if (!decl) return [];
              return [viewFactRowFor(state, send, decl, entityId, host)];
            },
          },
        ),
      ],
    ),
  );

  return rows;
}

/**
 * §7.2 — the controlling player's identity editor: lean editable rows for the
 * baseline `player_authority` identity predicates (name / appearance / portrait)
 * that resolve to a declaration. Reuses the value-kind input + Save machinery
 * (the SAME dirty-epoch + `submit-fact` path as the GM editor); the DO
 * op-accept gate is the real authority. Rendered on the player surface only
 * when the player controls the Character (gated by the caller).
 */
const PLAYER_IDENTITY_KINDS: ReadonlySet<string> = new Set([
  'text',
  'rich_text',
  'asset',
  'image',
]);

export function viewPlayerAuthorityFactRows<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  entityId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  return div({ class: 'player-authority-rows', 'data-testid': 'player-authority-rows' }, [
    each(
      // One keyed row per player-authority predicate, keyed `<pid>#<epoch>`. The
      // homebrew epoch remounts rows when the registry overlay loads/changes —
      // crucial here because the official-core identity predicates (appearance /
      // portrait) resolve only once the campaign pack registry is ready, AFTER
      // first mount; the built-in `name` resolves immediately. Reading
      // `traitsAtId` ties the list to a tracked state path so it re-runs on the
      // `entities-rebuilt` fold that also (re)loads the registry.
      state.map((s) => {
        const epoch = homebrewEpoch();
        void traitsAtId(s, entityId);
        return [...playerAuthorityPredicateIds()].map((pid) => `${pid}#${epoch}`);
      }),
      {
        key: (k) => k,
        render: (rowKeySig: Signal<string>): Renderable => {
          const pid = peekRowPredicate(rowKeySig);
          const decl = loadGmRegistry().lookup(pid);
          if (!decl || !PLAYER_IDENTITY_KINDS.has(decl.value_kind[0] ?? 'text')) return [];
          return [viewFactRowFor(state, send, decl, entityId, host, true)];
        },
      },
    ),
  ]);
}

// ────────────────────────────────────────────────────────────────────
// Save-button commit dispatch.
//
// The Save button reads the current input value(s) from the DOM and
// dispatches the right edit-fact / soft-delete-fact Msg per kind.
// Mirrors the per-kind commit logic in each viewXxxInput; centralised
// here so the explicit Save affordance and Enter share the same path.
//
// Returns `false` if this kind doesn't surface a Save button (boolean
// and enum commit on change, so they have nothing to "save later").
// ────────────────────────────────────────────────────────────────────

/**
 * Returns true iff the row has an unpersisted user edit — the buffered
 * (typed) value differs from the currently-saved fact value. Used by
 * the save button's `disabled` accessor so "type X then backspace"
 * leaves the button inert (no fictitious save). Same-module function
 * declaration so the cross-file walker resolves it from reactive
 * accessors statically.
 */
function rowHasUnsavedEdit<S extends FactRowStateShape>(
  s: S,
  entityId: string,
  predicate: string,
  rowKey: string,
): boolean {
  if (!factDirtyAtKey(s, rowKey)) return false;
  const buffered = inputBufferAtKey(s, rowKey);
  // No buffer means the input type doesn't fold its live value into
  // `inputBuffer[key]` on every keystroke (date/time/range/multi controls
  // commit through their own composite paths). Fall back to the dirty flag
  // alone for those controls. Text and number inputs use the comparison
  // below so "type X then backspace" does not leave a fictitious save.
  if (buffered === undefined) return true;
  const current = currentValue(factAtPredicate(s, entityId, predicate));
  const currentStr =
    current === undefined || current === null ? '' : String(current);
  return buffered !== currentStr;
}

/**
 * Whether a row shows the "Save" button + the "Press Enter or click Save"
 * hint. Only the "type then commit" inputs qualify — text, number, and the
 * asset-id text inputs. Everything else commits on its own gesture and a
 * Save button / Enter hint would be confusing or wrong:
 *   - `rich_text` (WYSIWYG editor) commits on blur; Enter inserts a newline.
 *   - `enum` / `ref` (select) and `boolean` (checkbox) commit on change.
 *   - the native date/time pickers and the structured game-time + tuple
 *     editors commit on change and have composite DOM `commitRowFromDom`
 *     can't read.
 */
function kindSupportsSaveButton(decl: PredicateDeclaration): boolean {
  const kind = decl.value_kind[0] ?? 'text';
  const MANUAL_SAVE: ReadonlySet<ValueKind> = new Set<ValueKind>([
    'text',
    'rich_text',
    'number',
    'asset',
    'image',
    'file',
  ]);
  // Containment is a structured editor that commits on its own change gestures
  // (parent select, context input on change, position fields) — a Save button
  // reading flat DOM inputs can't reconstruct it.
  if (kind === 'containment') return false;
  if (hasObjectUniquenessKey(decl)) {
    // Multi ref-pickers commit on selection-change; other multi rows
    // (text / number) commit on Enter and benefit from the Save button.
    return kind !== 'ref' && kind !== 'enum' && kind !== 'boolean';
  }
  return MANUAL_SAVE.has(kind);
}

/**
 * Whether to show the "Press Enter or click Save" hint. A superset gate of
 * the Save button minus the kinds where Enter does NOT save: the rich-text
 * editor (Enter inserts a newline; it commits on blur / Save button), and
 * the asset/image/file pickers (no text entry — upload commits directly).
 * Keeping the hint honest avoids telling the user to press a key that does
 * something else.
 */
function kindShowsSaveHint(decl: PredicateDeclaration): boolean {
  if (!kindSupportsSaveButton(decl)) return false;
  const kind = decl.value_kind[0] ?? 'text';
  return kind !== 'rich_text' && kind !== 'asset' && kind !== 'image' && kind !== 'file';
}

/**
 * LLM author mode (Phase B): the ✨ "Draft with AI" button is offered
 * only on single-cardinality prose (`rich_text`) predicates — the worker
 * suggest-prose route accepts prose predicates, and free text is where
 * AI drafting is useful. Refs/enums/numbers/dates are out of scope.
 */
function kindSupportsAiDraft(decl: PredicateDeclaration): boolean {
  return !hasObjectUniquenessKey(decl) && decl.value_kind[0] === 'rich_text';
}

/** Phase D — the ✨ "Generate image" affordance is offered on single asset/image predicates. */
function kindSupportsAiImage(decl: PredicateDeclaration): boolean {
  const kind = decl.value_kind[0];
  return !hasObjectUniquenessKey(decl) && (kind === 'asset' || kind === 'image');
}

function commitRowFromDom<Model, Msg>(
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): void {
  const predicate = decl.id;
  const kind = decl.value_kind[0] ?? 'text';

  if (hasObjectUniquenessKey(decl)) {
    // Multi: collect all <input id="${fieldId}-${idx}"> in order; drop
    // trailing empties so soft-delete fires for an emptied list.
    const root = document.getElementById(fieldId);
    if (!root) return;
    const inputs = Array.from(
      root.querySelectorAll<HTMLInputElement>(`input[id^="${fieldId}-"]`),
    );
    const values = inputs.map((el) => el.value);
    if (values.length === 0 || values.every((v) => v === '')) {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    send(host.editFact(entityId, predicate, values));
    return;
  }

  if (kind === 'real_datetime_range') {
    const fromEl = document.getElementById(`${fieldId}-from`) as HTMLInputElement | null;
    const toEl = document.getElementById(`${fieldId}-to`) as HTMLInputElement | null;
    const from = fromEl?.value ?? '';
    const to = toEl?.value ?? '';
    if (from === '' && to === '') {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    if (from === '' || to === '') {
      send(host.invalidFact(entityId, predicate, t('entity.invalid_datetime_range_partial')));
      return;
    }
    const fromIso = localDatetimeToIso(from);
    const toIso = localDatetimeToIso(to);
    if (Date.parse(toIso) < Date.parse(fromIso)) {
      send(host.invalidFact(entityId, predicate, t('entity.invalid_datetime_range_order')));
      return;
    }
    const value = { from: fromIso, to: toIso };
    const result = validateValueWithDeclaration(decl, value);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
    return;
  }

  const el = document.getElementById(fieldId) as
    | HTMLInputElement
    | HTMLTextAreaElement
    | null;
  if (!el) return;
  const raw = el.value;

  if (raw === '') {
    send(host.softDeleteFact(entityId, predicate));
    return;
  }

  switch (kind) {
    case 'rich_text':
    case 'asset':
    case 'image':
    case 'file':
    case 'real_date':
    case 'real_time':
      send(host.editFact(entityId, predicate, raw));
      return;
    case 'number': {
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        send(host.invalidFact(entityId, predicate, t('entity.invalid_number')));
        return;
      }
      // Pair with the unit picker (if any): a chosen unit → store the
      // dimension's canonical base value with the unit as provenance
      // (ADR-0007); no unit → a bare number.
      const unitEl = document.getElementById(`${fieldId}-unit`) as HTMLSelectElement | null;
      const unitId = unitEl?.value ?? '';
      let value: number | { canonical_value: string; unit_id: string } = n;
      if (unitId) {
        const unitDef = campaignUnitRegistry().unit(unitId);
        if (!unitDef) {
          send(host.invalidFact(entityId, predicate, t('entity.invalid_number')));
          return;
        }
        value = { canonical_value: toCanonicalValue(raw.trim(), unitDef), unit_id: unitId };
      }
      const result = validateValueWithDeclaration(decl, value);
      if (!result.ok) {
        send(host.invalidFact(entityId, predicate, result.error));
        return;
      }
      send(host.editFact(entityId, predicate, result.value));
      return;
    }
    case 'real_datetime': {
      const iso = localDatetimeToIso(raw);
      const result = validateValueWithDeclaration(decl, iso);
      if (!result.ok) {
        send(host.invalidFact(entityId, predicate, result.error));
        return;
      }
      send(host.editFact(entityId, predicate, result.value));
      return;
    }
    default: {
      // text / ref — validate via declaration.
      const result = validateValueWithDeclaration(decl, raw);
      if (!result.ok) {
        send(host.invalidFact(entityId, predicate, result.error));
        return;
      }
      send(host.editFact(entityId, predicate, result.value));
    }
  }
}

// ────────────────────────────────────────────────────────────────────
// Single fact-row dispatch.
//
// Picks the input shape from `decl.value_kind[0]` and `cardinality`.
// Polymorphic predicates (multi-kind) currently render against the
// first declared kind; the auditor's per-fact diagnostics surface
// any mismatch.
// ────────────────────────────────────────────────────────────────────

/**
 * As-of scrubber authoring gate (ADR-0005 §3, #192).
 *
 * Returns true when the row's "new at now" authoring must be locked: the
 * browse cursor diverges from the session "now" AND the row has no existing
 * fact (a brand-new value would be mis-stamped at the wrong in-world day).
 * A row that already holds a fact is NEVER locked — editing it in place is a
 * "correct/refine" of an existing segment, which carries explicit validity
 * and is permitted while browsing.
 */
function authoringLockedForRow<Model extends FactRowStateShape, Msg>(
  s: Model,
  entityId: string,
  predicate: string,
  host: FactRowHost<Model, Msg>,
): boolean {
  if (host.browseCursor(s) === null) return false; // browsing "now" → not locked
  // An existing fact (any segment) means edits are "correct/refine" → allowed.
  return currentValue(factAtPredicate(s, entityId, predicate)) === undefined;
}

function viewFactRowFor<Model extends FactRowStateShape, Msg>(
  rawState: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  host: FactRowHost<Model, Msg>,
  // `lean` renders a player-facing identity row (§7.2): label + value input +
  // Save, WITHOUT the GM-oriented AI-draft / AI-image affordances or the as-of
  // authoring-lock. The server still enforces who may write (op-accept gate).
  lean = false,
): Mountable {
  // Row-context rebasing (llui 0.5.x).
  //
  // Fact rows are produced inside the trait-row keyed-`each` in
  // `viewEntityFactRows`, which is itself nested inside the entity-page
  // `each` (app.ts route dispatch). A leaf `state.map((s) => …)` built in a
  // row body is a plain `pathHandle.map`: it does NOT consult the runtime's
  // row-context hooks, so at commit time its `produce` is fed the COMBINED
  // row ctx (`{ item, state, index }`) instead of the component Model. The
  // accessors then read `s.entities` / `s.inputBuffer` off the ctx bag —
  // which is `undefined` — and throw `Cannot read properties of undefined
  // (reading '<entityId>/<predicate>')`. (The top-level `name` row escaped
  // because the outer-each row ctx's `.state` happens to coincide there.)
  //
  // `derived([state], (s) => s)` IS row-aware: built inside a row
  // (`__inRowBuild()` true), it rebases the component-state input to read
  // `ctx.state`, and the rebased deps are row-local — so every `.map(…)`
  // chained off this handle inherits the correct `ctx.state` resolution
  // regardless of nesting depth. At top level (no row) `derived` is the
  // identity, so this is safe for every call site. Threading the wrapped
  // signal everywhere below fixes the whole row subtree in one place.
  const state = derived([rawState], (s) => s);
  const predicate = decl.id;
  const fieldId = `fact-input-${predicate}`;
  const labelText = predicateLabel(predicate);
  const rowKey = factRowKey(entityId, predicate);
  // Structural one-shot: choose the input control from the stored value's shape
  // (point vs in-world span, #221). Snapshot at the render boundary; the helper
  // is pure (plain-in). The row is keyed on the predicate and no UI path flips
  // a point into a span in place, so a build-time read is correct.
  const inputKind = resolveInputKind(decl, state.peek(), entityId);

  // Reactive accessors that the llui compile-time path analyzer can
  // trace. Each one delegates to a bare-Identifier function imported
  // from `./fact-row-state.ts` — those declarations read direct
  // `s.<field>` paths (the only shape the analyzer follows), so the
  // compiler's `__prefixes` table picks them up and Phase 2 binding
  // evaluation gates correctly. Pre-refactor these went through
  // `host.<method>(s, e, p)`, which the analyzer treated as opaque
  // — every binding fell back to FULL_MASK but the commit dirty mask
  // stayed 0, leaving the DOM stale across keystrokes (the e2e
  // regression in 09-create-edit-navigate.spec.ts §9.4).
  // All reactive accessors below inline the bare-Identifier state-pick
  // functions directly (`factDirtyAtKey(s, rowKey)` etc.) — wrapping them
  // in `const dirtyAccessor = (s) => factDirtyAtKey(s, rowKey)` opacifies
  // the cross-file walker because closure-captured parameters
  // (`rowKey`, `entityId`, `predicate`) are unresolvable from another
  // module's analysis. Inlining at every use lets the walker resolve
  // each read path through the bare-Identifier declarations.

  // The row carries a `data-dirty` / `data-saved` attribute so CSS can
  // tint the input border without us having to thread classNames into
  // every per-kind primitive.
  return div(
    {
      class: 'fact-row',
      'data-value-kind': decl.value_kind[0] ?? 'text',
      'data-predicate': predicate,
      // Which attached trait this row came from, and whether that trait is the
      // one the GM is currently pointing at in the strip above. The pair is
      // what lets a chip highlight exactly the rows it owns — the two were
      // previously unconnected on the page.
      'data-trait': state.map(
        (s) => owningTraitFor(predicate, traitsAtId(s, entityId), getTraitIndex()) ?? '',
      ),
      'data-trait-highlight': state.map((s) => {
        const owner = owningTraitFor(predicate, traitsAtId(s, entityId), getTraitIndex());
        return owner !== null && owner === s.highlightedTrait ? 'true' : 'false';
      }),
      // 30 §7.1 — row-level status overlay. CSS in styles.css can tint
      // the whole row by `data-status` so the status pill in the chip
      // stack is not the only signal (a row that's draft is visually
      // draft top-to-bottom). 'canon' is the default and unstyled.
      'data-status': state.map(
        (s) => factAtPredicate(s, entityId, predicate)?.status ?? 'canon',
      ),
      'data-dirty': state.map((s) => (factDirtyAtKey(s, rowKey) ? 'true' : 'false')),
      'data-saved-recently': state.map((s) =>
        factSavedAtKey(s, rowKey) !== undefined && !factDirtyAtKey(s, rowKey)
          ? 'true'
          : 'false',
      ),
      // As-of scrubber authoring gate (ADR-0005 §3, #192): while the browse
      // cursor diverges from "now", creating a NEW value (an empty row) is
      // locked — it would be mis-stamped at the wrong in-world day. Editing an
      // EXISTING segment in place (correct/refine/close) stays allowed (it
      // carries explicit validity → no timestamp ambiguity), so a row that
      // already holds a fact is NEVER locked. CSS dims the locked row; the
      // visible notice below is the non-color cue + the input is disabled.
      'data-authoring-locked': state.map((s) =>
        authoringLockedForRow(s, entityId, predicate, host) ? 'true' : 'false',
      ),
    },
    [
      label({ for: fieldId, class: 'fact-label' }, [
        // #156 — leading dot when dirty (color paired with the dot
        // glyph so it survives colour-blind viewers per the
        // structural a11y invariant). `aria-hidden` because the dot
        // is duplicated by the textual "Unsaved" cue further down.
        show(
          state.map((s) => factDirtyAtKey(s, rowKey)),
          () => [
            span(
              {
                class: 'fact-dirty-dot',
                'aria-hidden': 'true',
                'data-testid': `fact-dirty-dot-${predicate}`,
              },
              ['●'],
            ),
          ],
        ),
        text(labelText),
      ]),
      ...viewInputForKind(state, send, decl, entityId, fieldId, host, inputKind),
      // As-of authoring lock notice (#192) — shown only for an empty row while
      // browsing the past. Non-color: a lock glyph paired with explanatory
      // text (role=note, polite live region). Suppressed in lean (player) rows.
      ...(lean
        ? []
        : [show(
        state.map((s) => authoringLockedForRow(s, entityId, predicate, host)),
        () => [
          div(
            {
              class: 'fact-authoring-lock',
              role: 'note',
              'aria-live': 'polite',
              'data-testid': `fact-authoring-lock-${predicate}`,
            },
            [
              span({ class: 'fact-authoring-lock-glyph', 'aria-hidden': 'true' }, ['\u{1F512}']),
              span({ class: 'fact-authoring-lock-text' }, [text(t('scrubber.author_locked'))]),
            ],
          ),
        ],
      )]),
      // (The explicit Save button moved into the right-aligned `.fact-rail`
      // below — see the rail block. Kept out of the value column.)
      // LLM author mode — ✨ "Draft with AI" on prose rows. Offered on
      // both empty AND filled fields: a draft on a filled field proposes
      // a REPLACEMENT (it shadows canon on the chain), and rejecting it
      // restores the prior canon value (lossless — see the reject-draft
      // reducer arm). Disabled while a suggest is in flight; the drafted
      // value lands in the Draft Inbox. An error span (role=alert)
      // surfaces failures next to the button.
      ...(!lean && kindSupportsAiDraft(decl)
        ? [
            uiButton({
              variant: 'secondary',
              type: 'button',
              className: 'fact-ai-draft-button',
              testid: `fact-ai-draft-${predicate}`,
              ariaLabel: t('entity.facts.ai_draft'),
              disabled: state.map((s) => aiDraftPendingAtKey(s, rowKey)),
              onClick: () => send(host.draftWithAi(entityId, predicate)),
              children: [
                text(
                  state.map((s) =>
                    aiDraftPendingAtKey(s, rowKey)
                      ? t('entity.facts.ai_drafting')
                      : t('entity.facts.ai_draft'),
                  ),
                ),
              ],
            }),
            span(
              {
                class: 'fact-ai-draft-error',
                role: 'alert',
                'data-testid': `fact-ai-draft-error-${predicate}`,
              },
              [text(state.map((s) => aiDraftErrorAtKey(s, rowKey)))],
            ),
          ]
        : []),
      // ✨ "Generate image" on asset rows (portrait / hero). Works on
      // empty rows (first portrait) and filled rows (regenerate); the
      // optional direction input steers the art. Lossless-reject like prose.
      ...(!lean && kindSupportsAiImage(decl)
        ? [
            div({ class: 'fact-ai-image-controls' }, [
              textInput({
                className: 'fact-ai-image-prompt',
                testid: `fact-ai-image-prompt-${predicate}`,
                placeholder: t('entity.facts.ai_image_prompt_placeholder'),
                ariaLabel: t('entity.facts.ai_image_prompt_label'),
                attrs: { id: `fact-ai-image-prompt-${predicate}` },
              }),
              uiButton({
                variant: 'secondary',
                type: 'button',
                className: 'fact-ai-draft-button',
                testid: `fact-ai-image-${predicate}`,
                ariaLabel: t('entity.facts.ai_image'),
                disabled: state.map((s) => aiDraftPendingAtKey(s, rowKey)),
                onClick: () => {
                  const el = document.getElementById(
                    `fact-ai-image-prompt-${predicate}`,
                  ) as HTMLInputElement | null;
                  send(host.generateImageFor(entityId, predicate, el?.value ?? ''));
                },
                children: [
                  text(
                    state.map((s) =>
                      aiDraftPendingAtKey(s, rowKey)
                        ? t('entity.facts.ai_image_pending')
                        : t('entity.facts.ai_image'),
                    ),
                  ),
                ],
              }),
            ]),
            span(
              {
                class: 'fact-ai-draft-error',
                role: 'alert',
                'data-testid': `fact-ai-image-error-${predicate}`,
              },
              [text(state.map((s) => aiDraftErrorAtKey(s, rowKey)))],
            ),
          ]
        : []),
      // #156 / mock §9.1 — right-aligned status RAIL. Pulled out of the value
      // column so the row reads tabular: the Save affordance sits beside the
      // value, and the edit-state cue (Editing / Unsaved / Saved) reads down
      // the right edge. The aria-live region inside keeps the SR save
      // announcement; the children render conditionally + mutually exclusive.
      div({ class: 'fact-rail' }, [
        // Explicit Save button. The button is `disabled` when the row isn't
        // dirty so the affordance is visible but inert until there's something
        // to commit. Hidden for value-kinds that commit on change (boolean,
        // enum). Replaces the prior blur-to-save autosave (GM found focus-loss
        // saves surprising).
        ...(kindSupportsSaveButton(decl)
          ? [
              uiButton({
                variant: 'primary',
                type: 'button',
                className: 'fact-save-button',
                testid: `fact-save-button-${predicate}`,
                ariaLabel: t('entity.facts.save_button'),
                disabled: state.map((s) => !rowHasUnsavedEdit(s, entityId, predicate, rowKey)),
                onClick: () => commitRowFromDom(send, decl, entityId, fieldId, host),
                children: [text(t('entity.facts.save_button'))],
              }),
            ]
          : []),
        div(
          {
            class: 'fact-save-status',
            'data-testid': `fact-save-status-${predicate}`,
          },
          [
            // Edit-state lifecycle cues — mutually exclusive, right-aligned
            // (mock §9.1). Priority: Saving → Unsaved → Editing → Saved.
            //
            // Editing — the row is focused with nothing pending / saving / just
            // saved (the calm "this row is being edited" state).
            show(
              state.map(
                (s) =>
                  focusedFactKeyOf(s) === rowKey &&
                  !factDirtyAtKey(s, rowKey) &&
                  !factSavingAtKey(s, rowKey) &&
                  factSavedAtKey(s, rowKey) === undefined,
              ),
              () => [
                span(
                  {
                    class: 'fact-save-cue fact-save-cue-editing',
                    'data-testid': `fact-save-editing-${predicate}`,
                  },
                  [text(t('entity.facts.editing_cue'))],
                ),
              ],
            ),
            // Saving — a commit is in flight (between Unsaved and Saved).
            // Outranks the dirty cue (the dirty flag stays set until the ACK).
            show(
              state.map((s) => factSavingAtKey(s, rowKey)),
              () => [
                span(
                  {
                    class: 'fact-save-cue fact-save-cue-saving',
                    'data-testid': `fact-save-saving-${predicate}`,
                  },
                  [text(t('entity.facts.saving_cue'))],
                ),
              ],
            ),
            // Dirty cue. Visible to sighted users; the dirty dot in the label
            // is the colour-paired non-text signal. Hidden while saving (the
            // "Saving…" cue takes over the in-flight window).
            show(
              state.map((s) => factDirtyAtKey(s, rowKey) && !factSavingAtKey(s, rowKey)),
              () => [
                span(
                  {
                    class: 'fact-save-cue fact-save-cue-dirty',
                    'data-testid': `fact-save-dirty-${predicate}`,
                  },
                  [text(t('entity.facts.dirty_cue'))],
                ),
              ],
            ),
            // Saved cue with aria-live="polite" — the text content
            // only renders for ~1500ms after a successful save, then
            // the `fact-saved-clear` reducer arm drops `savedAt` and
            // this `show` collapses.
            div(
              {
                class: 'fact-save-cue-live',
                'aria-live': 'polite',
                'aria-atomic': 'true',
              },
              [
                show(
                  state.map(
                    (s) =>
                      factSavedAtKey(s, rowKey) !== undefined && !factDirtyAtKey(s, rowKey),
                  ),
                  () => [
                    span(
                      {
                        class: 'fact-save-cue fact-save-cue-saved',
                        'data-testid': `fact-save-saved-${predicate}`,
                      },
                      [
                        span({ 'aria-hidden': 'true', class: 'fact-save-glyph' }, ['✓']),
                        text(` ${t('entity.facts.saved_cue')}`),
                      ],
                    ),
                  ],
                ),
              ],
            ),
          ],
        ),
      ]),
      // First-focus hint. Kept in the value column (a one-time onboarding strip,
      // too wide for the narrow status rail). Only renders for the
      // currently-focused row + while the hint hasn't been dismissed — AND only
      // for kinds that have the Enter/Save commit gesture (the markdown editor
      // commits on blur; selects/checkboxes/date pickers commit on their own
      // gesture, so "Press Enter or click Save" would be wrong there).
      ...(kindShowsSaveHint(decl)
        ? [
            show(
              state.map((s) => !factHintDismissedOf(s) && focusedFactKeyOf(s) === rowKey),
              () => [
                div(
                  {
                    class: 'fact-save-hint',
                    role: 'note',
                    'data-testid': `fact-save-hint-${predicate}`,
                  },
                  [
                    span({ class: 'fact-save-hint-text' }, [text(t('entity.facts.save_hint'))]),
                    uiButton({
                      variant: 'icon',
                      type: 'button',
                      className: 'fact-save-hint-dismiss',
                      testid: 'fact-save-hint-dismiss',
                      ariaLabel: t('entity.facts.save_hint_dismiss'),
                      // The hint is shown only while the row's input is
                      // focused, so the blur guard is what makes the dismissal
                      // reachable at all. See `ButtonOptions.preventBlur`.
                      preventBlur: true,
                      onClick: () => send(host.dismissHint()),
                      children: [uiIcon('x', { size: 14 })],
                    }),
                  ],
                ),
              ],
            ),
          ]
        : []),
      show(
        state.map((s) => Boolean(inlineErrorAtKey(s, rowKey))),
        () => [
          span(
            {
              class: 'inline-error',
              role: 'alert',
              id: `${fieldId}-error`,
              'data-testid': `fact-error-${predicate}`,
            },
            [text(state.map((s) => inlineErrorAtKey(s, rowKey) ?? ''))],
          ),
        ],
      ),
      // #146 follow-up — the row's meta chips (reveal indicator, status,
      // provenance) + the validity pill cluster on a SINGLE wrapping line
      // (mock entity-page.html §7.1: the chips sit in the value column's
      // chip cluster, not stacked block-after-block). The wrapper is purely
      // a layout shell — every chip keeps its own `data-testid` + reactive
      // accessor, so editing behaviour and the a11y grayscale audit hooks
      // are untouched.
      //
      // The History affordance joins that same line as a quiet icon trigger.
      // It used to be an inline `<details>` block ABOVE this row, which made
      // one fact read as three stacked strips (history / chips / validity)
      // instead of one. The versions themselves now open in a dialog.
      div(
        { class: 'fact-meta', 'data-testid': `fact-meta-${predicate}` },
        [
          // Which trait brought this row in. Quiet, always present — the
          // durable half of the chip↔row relationship (the hover highlight is
          // the transient half). Without it a row gives no clue which trait
          // owns it, so detaching a trait is a guess about what disappears.
          show(
            state.map(
              (s) => owningTraitFor(predicate, traitsAtId(s, entityId), getTraitIndex()) !== null,
            ),
            () => [
              span(
                { class: 'fact-row-trait-label', 'data-testid': `fact-trait-label-${predicate}` },
                [
                  uiIcon('diamond', { size: 11 }),
                  text(
                    state.map((s) => {
                      const owner = owningTraitFor(
                        predicate,
                        traitsAtId(s, entityId),
                        getTraitIndex(),
                      );
                      return owner === null ? '' : traitLabel(owner);
                    }),
                  ),
                ],
              ),
            ],
          ),
          show(
            state.map((s) => (factAtPredicate(s, entityId, predicate)?.versions.length ?? 0) > 1),
            () => viewHistory(state, send, decl.id, entityId, host),
          ),
          ...viewFactChips(state, send, decl.id, entityId, host),
          ...viewValidityRow(state, send, decl.id, entityId, host),
        ],
      ),
      ...viewSegmentStrip(state, send, decl, entityId, host),
    ],
  );
}

// ────────────────────────────────────────────────────────────────────
// Value-kind dispatch — picks the right input primitive.
//
// `cardinality: 'many'` wraps the primitive in a list with add /
// remove controls. The value stored on the substrate is an array of
// the primitive kind's value type.
// ────────────────────────────────────────────────────────────────────

/**
 * Resolve the input kind for a (possibly polymorphic) declaration from a plain
 * Model SNAPSHOT — pure (plain-in / plain-out, no live signal). Normally the
 * first declared kind, but an in-world date predicate declared point-first with
 * a range alternative (`occurred_at` = `[game_date, game_date_range]`) renders
 * the RANGE control when the stored value is a `{ from, to }` span, so a
 * span-dated event edits as a span rather than collapsing to the empty point
 * control. The caller peeks the snapshot at the render boundary. #221.
 */
function resolveInputKind<Model extends FactRowStateShape>(
  decl: PredicateDeclaration,
  snap: Model,
  entityId: string,
): ValueKind {
  const first = decl.value_kind[0] ?? 'text';
  const rangeKind = decl.value_kind.find(
    (k) => k === 'game_datetime_range' || k === 'game_date_range',
  );
  if (rangeKind !== undefined && rangeKind !== first) {
    const v = currentValue(factAtPredicate(snap, entityId, decl.id));
    if (v !== null && typeof v === 'object' && 'from' in v && 'to' in v) return rangeKind;
  }
  return first;
}

function viewInputForKind<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
  kind: ValueKind,
): Renderable {
  // Containment authors a STRUCTURED value (`{ parent, context, position? }`)
  // even though it is object-keyed (multi-context). The scalar `viewMultiInput`
  // array list can't express it, so it gets its own editor regardless of
  // uniqueness — the dedicated control commits one well-formed containment
  // value; multiple contexts coexist as separate fact instances (#206).
  if (kind === 'containment') {
    return [viewContainmentInput(state, send, decl, entityId, fieldId, host)];
  }
  // `recurrence_rule` (`recurs_as`) is likewise a STRUCTURED value the scalar
  // multi-list can't express; it gets the dedicated anchor-authoring control
  // (B5 / #238) even though it is object-keyed. Multiple concurrent /
  // successive rules coexist as separate fact instances via the same
  // object-uniqueness machinery (06-timeline.md §10.2), exactly like
  // containment.
  if (kind === 'recurrence_rule') {
    return [viewRecurrenceRuleInput(state, send, decl, entityId, fieldId, host)];
  }
  if (hasObjectUniquenessKey(decl)) {
    return [viewMultiInput(state, send, decl, entityId, fieldId, kind, host)];
  }
  return [viewSingleInput(state, send, decl, entityId, fieldId, kind, host)];
}

function viewSingleInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  kind: ValueKind,
  host: FactRowHost<Model, Msg>,
): Mountable {
  switch (kind) {
    case 'text':
      return viewTextLikeInput(state, send, decl, entityId, fieldId, kind, host);
    case 'ref':
      // Relationship authoring: a picker of existing entities (filtered
      // to the predicate's allowed roots), value = target entity id.
      return viewRefInput(state, send, decl, entityId, fieldId, host);
    case 'rich_text':
      return viewRichTextInput(state, send, decl, entityId, fieldId, host);
    case 'number':
      return viewNumberInput(state, send, decl, entityId, fieldId, host);
    case 'boolean':
      return viewBooleanInput(state, send, decl, entityId, fieldId, host);
    case 'enum':
      return viewEnumInput(state, send, decl, entityId, fieldId, host);
    case 'color':
      return viewColorInput(state, send, decl, entityId, fieldId, host);
    case 'geometry_point':
      return viewGeometryPointInput(state, send, decl, entityId, fieldId, host);
    case 'geometry_line':
    case 'geometry_polygon':
      return viewGeometryPathInput(state, send, decl, entityId, fieldId, kind, host);
    case 'interior_frame':
      return viewInteriorFrameInput(state, send, decl, entityId, fieldId, host);
    case 'containment':
      return viewContainmentInput(state, send, decl, entityId, fieldId, host);
    case 'asset':
    case 'image':
    case 'file':
      // image / file are asset-backed; the renderer differs only in the
      // upload `accept` constraint + whether it shows an image preview.
      return viewAssetInput(state, send, decl, entityId, fieldId, kind, host);
    case 'real_date':
      return viewDateTimeLikeInput(state, send, decl, entityId, fieldId, 'date', host);
    case 'real_time':
      return viewDateTimeLikeInput(state, send, decl, entityId, fieldId, 'time', host);
    case 'real_datetime':
      return viewDatetimeInput(state, send, decl, entityId, fieldId, host);
    case 'real_datetime_range':
      return viewDatetimeRangeInput(state, send, decl, entityId, fieldId, host);
    case 'game_date':
    case 'game_datetime':
    case 'game_time':
      return viewGameTimeInput(state, send, decl, entityId, fieldId, kind, host);
    case 'game_date_range':
    case 'game_datetime_range':
      return viewGameTimeRangeInput(state, send, decl, entityId, fieldId, kind, host);
    case 'tuple':
      return viewTupleInput(state, send, decl, entityId, fieldId, host);
    case 'recurrence_rule':
      // The dedicated anchor-authoring control (#238 / B5): pick a declared
      // cycle, set every_n, choose an AnchorSpec, optional location /
      // participants / duration — all populated from the active calendar's
      // declared cycles (no free text).
      return viewRecurrenceRuleInput(state, send, decl, entityId, fieldId, host);
  }
}

// ─── recurrence_rule option extraction (B5 / #238) ─────────────────
//
// The recurrence editor authors a rule against the active calendar's
// DECLARED cycles (06-timeline.md §10.1). Cycle / instance / label pickers
// are populated from the calendar's normalized cycle bag (`calendarBag`),
// so the GM never types a free-text cycle name — an unknown cycle could
// never resolve through the A2 engine. These helpers project the bag into
// plain option lists; they are pure reads of the calendar definition.

/** The declared cycle names in the active calendar's bag, in declared order. */
function recurrenceCycleNames(calendar: Calendar): string[] {
  return calendarBag(calendar).map((c) => c.name);
}

/** Resolve a declared cycle by name, or undefined when absent. */
function recurrenceCycle(calendar: Calendar, cycleName: string): CycleDefinition | undefined {
  return calendarBag(calendar).find((c) => c.name === cycleName);
}

/**
 * The named instances a `nested_position` step can target for `cycleName` —
 * the declared instance names of a nested cycle with an irregular
 * per-instance list (months), or the named instances of an open cycle. A
 * `regular` nested cycle has no per-instance names (every instance is
 * identical), so it offers none and the GM addresses the position by
 * ordinal instead. Returns the names that actually exist on the cycle (the
 * "validate against the chosen cycle" requirement).
 */
function recurrenceInstanceNames(calendar: Calendar, cycleName: string): string[] {
  const cycle = recurrenceCycle(calendar, cycleName);
  if (cycle === undefined) return [];
  const structure = cycle.structure;
  if (structure.kind === 'nested' && structure.pattern.kind === 'irregular') {
    return structure.pattern.per_instance
      .map((i) => i.name)
      .filter((n): n is string => typeof n === 'string' && n.length > 0);
  }
  if (structure.kind === 'open') {
    return structure.instances
      .map((i) => i.name)
      .filter((n): n is string => typeof n === 'string' && n.length > 0);
  }
  return [];
}

/** Cycle names that declare a label spec — the candidates for a `labeled` anchor. */
function recurrenceLabeledCycleNames(calendar: Calendar): string[] {
  return calendarBag(calendar)
    .filter((c) => c.labels !== undefined)
    .map((c) => c.name);
}

/**
 * The selectable labels of a labeled cycle. Only a `list` LabelSpec
 * enumerates its labels statically; `product` / `formula` labels are
 * computed (A4) and have no finite picker, so they offer none (the GM
 * targets those via the cycle's positions, not this picker).
 */
function recurrenceCycleLabels(calendar: Calendar, cycleName: string): string[] {
  const cycle = recurrenceCycle(calendar, cycleName);
  const labels = cycle?.labels;
  if (labels === undefined || labels.kind !== 'list') return [];
  return [...labels.labels];
}

/**
 * The `recurrence_rule` editor (06-timeline.md §10.1, B5 / #238): authors a
 * `recurs_as`-shaped value on a parent Lore/Event. Replaces the prior
 * read-only placeholder.
 *
 *   - **Cycle** — a select of the active calendar's DECLARED cycle names
 *     (`recurrenceCycleNames`). No free text — an unknown cycle could never
 *     resolve through the A2 runtime, so the picker is closed.
 *   - **every_n** — a positive integer (1 = every cycle).
 *   - **Anchor** — a select of the three `AnchorSpec` kinds; the visible
 *     sub-inputs switch on the chosen kind:
 *       · `day_of_cycle`    — an `offset` integer.
 *       · `nested_position` — up to two path steps, each a (cycle, instance)
 *         pair; the instance picker offers the chosen cycle's named
 *         instances (months / open-cycle names) or an ordinal when the cycle
 *         has none. Only instances that EXIST are offered.
 *       · `labeled`         — a label-bearing cycle + one of its declared
 *         `list` labels.
 *   - **at_location** — an optional Location ref (entity-id select).
 *   - **participants** — an optional multi-ref (a select per chosen
 *     participant + a trailing "add" select).
 *   - **duration.days** — an optional whole-day count.
 *
 * Every commit validates through `validateValueWithDeclaration` — a
 * malformed rule surfaces as an invalid fact, never a silent bad store. The
 * keyed `each` over the value digest (+ entity count, since the ref pickers
 * depend on the entity set) reseeds the uncontrolled sub-inputs when the
 * fact changes externally (AI draft, navigation, validity supersession).
 * The control respects the llui peek-in-slot / row-derived constraints: the
 * one-shot snapshot is read via `state.peek()` inside the `each` render body
 * (not in a reactive slot).
 */
function viewRecurrenceRuleInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  const cycleId = `${fieldId}-cycle`;
  const everyNId = `${fieldId}-every-n`;
  const anchorKindId = `${fieldId}-anchor-kind`;
  const offsetId = `${fieldId}-offset`;
  const npCycleId = (i: number): string => `${fieldId}-np-cycle-${i}`;
  const npInstanceId = (i: number): string => `${fieldId}-np-instance-${i}`;
  const labeledCycleId = `${fieldId}-labeled-cycle`;
  const labelId = `${fieldId}-label`;
  const atLocationId = `${fieldId}-at-location`;
  const participantId = (i: number): string => `${fieldId}-participant-${i}`;
  const durationDaysId = `${fieldId}-duration-days`;
  // nested_position authors at most two steps in this control (festival case:
  // month + day-of-month); a deeper path is not surfaced by the picker.
  const NP_STEPS = 2;

  const getEl = (id: string): HTMLInputElement | HTMLSelectElement | null =>
    document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
  const getVal = (id: string): string => getEl(id)?.value.trim() ?? '';

  // The three AnchorSpec sub-input groups are always present in the DOM (so
  // the GM can switch freely before any value is stored, and the keyed
  // `each` need not reseed mid-authoring). Visibility + the cycle-dependent
  // instance/label option lists are synced imperatively from the live
  // selects — a pure DOM read of the active calendar's declared cycles, no
  // model round-trip required to flip anchor kinds.
  const groupId = (kind: string): string => `${fieldId}-anchor-${kind}`;

  /** Show only the active anchor kind's group. */
  const syncAnchorVisibility = (): void => {
    const active = getVal(anchorKindId) || 'day_of_cycle';
    for (const kind of ['day_of_cycle', 'nested_position', 'labeled']) {
      const g = document.getElementById(groupId(kind));
      if (g === null) continue;
      if (kind === active) g.removeAttribute('hidden');
      else g.setAttribute('hidden', 'hidden');
    }
  };

  /**
   * Repopulate a nested-position step's instance picker from the cycle
   * chosen in that step (declared instance names only). A cycle with no
   * named instances offers a free numeric ordinal instead (the picker
   * becomes a number input — handled by `setInstanceMode`).
   */
  const syncInstanceOptions = (i: number): void => {
    const calendar = host.activeCalendar(state.peek());
    const cycleName = getVal(npCycleId(i));
    const names = recurrenceInstanceNames(calendar, cycleName);
    const sel = getEl(npInstanceId(i));
    const wrap = document.getElementById(`${npInstanceId(i)}-wrap`);
    if (wrap === null) return;
    const prev = sel?.value ?? '';
    if (names.length > 0) {
      const node = document.createElement('select');
      node.id = npInstanceId(i);
      node.className = 'dl-select';
      node.setAttribute('data-testid', `fact-${predicate}-np-instance-${i}`);
      const ph = document.createElement('option');
      ph.value = '';
      ph.textContent = t('fact.recurrence.instance_placeholder');
      node.appendChild(ph);
      for (const n of names) {
        const o = document.createElement('option');
        o.value = n;
        o.textContent = n;
        if (n === prev) o.selected = true;
        node.appendChild(o);
      }
      node.addEventListener('change', commit);
      wrap.replaceChildren(node);
    } else {
      const node = document.createElement('input');
      node.type = 'number';
      node.id = npInstanceId(i);
      node.className = 'fact-recurrence-num';
      node.step = '1';
      node.placeholder = t('fact.recurrence.ordinal_placeholder');
      node.setAttribute('data-testid', `fact-${predicate}-np-instance-${i}`);
      if (prev !== '' && /^-?\d+$/.test(prev)) node.value = prev;
      node.addEventListener('change', commit);
      wrap.replaceChildren(node);
    }
  };

  /** Repopulate the label picker from the chosen labeled cycle's list labels. */
  const syncLabelOptions = (): void => {
    const calendar = host.activeCalendar(state.peek());
    const cycleName = getVal(labeledCycleId);
    const labels = recurrenceCycleLabels(calendar, cycleName);
    const sel = getEl(labelId) as HTMLSelectElement | null;
    if (sel === null) return;
    const prev = sel.value;
    const ph = document.createElement('option');
    ph.value = '';
    ph.textContent = t('fact.recurrence.label_placeholder');
    const opts = [ph];
    for (const lbl of labels) {
      const o = document.createElement('option');
      o.value = lbl;
      o.textContent = lbl;
      if (lbl === prev) o.selected = true;
      opts.push(o);
    }
    sel.replaceChildren(...opts);
  };

  const commit = (): void => {
    send(host.markDirty(entityId, predicate));
    const cycle = getVal(cycleId);
    // An empty cycle ⇒ the rule is incomplete; clear the fact entirely.
    if (cycle === '') {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }

    const everyNRaw = getVal(everyNId);
    const everyN = everyNRaw === '' ? 1 : Number(everyNRaw);

    const anchorKind = getVal(anchorKindId);
    let anchor: Record<string, unknown>;
    if (anchorKind === 'day_of_cycle') {
      const offsetRaw = getVal(offsetId);
      if (offsetRaw === '' || !Number.isFinite(Number(offsetRaw))) {
        send(host.invalidFact(entityId, predicate, t('fact.recurrence.invalid_offset')));
        return;
      }
      anchor = { kind: 'day_of_cycle', offset: Number(offsetRaw) };
    } else if (anchorKind === 'nested_position') {
      const path: { cycle: string; instance: string | number }[] = [];
      for (let i = 0; i < NP_STEPS; i++) {
        const stepCycle = getVal(npCycleId(i));
        const stepInstance = getVal(npInstanceId(i));
        if (stepCycle === '' && stepInstance === '') continue;
        if (stepCycle === '' || stepInstance === '') {
          send(host.invalidFact(entityId, predicate, t('fact.recurrence.invalid_path')));
          return;
        }
        // The instance is an ordinal when it parses as an integer, else a
        // declared instance name (both forms are valid per §10.1).
        const asNum = Number(stepInstance);
        const instance =
          stepInstance !== '' && Number.isInteger(asNum) && String(asNum) === stepInstance
            ? asNum
            : stepInstance;
        path.push({ cycle: stepCycle, instance });
      }
      if (path.length === 0) {
        send(host.invalidFact(entityId, predicate, t('fact.recurrence.invalid_path')));
        return;
      }
      anchor = { kind: 'nested_position', path };
    } else if (anchorKind === 'labeled') {
      const labeledCycle = getVal(labeledCycleId);
      const lbl = getVal(labelId);
      if (labeledCycle === '' || lbl === '') {
        send(host.invalidFact(entityId, predicate, t('fact.recurrence.invalid_label')));
        return;
      }
      anchor = { kind: 'labeled', cycle: labeledCycle, label: lbl };
    } else {
      send(host.invalidFact(entityId, predicate, t('fact.recurrence.invalid_anchor')));
      return;
    }

    const value: Record<string, unknown> = { cycle, every_n: everyN, anchor };

    const atLocation = getVal(atLocationId);
    if (atLocation !== '') value['at_location'] = atLocation;

    const participants: string[] = [];
    // Participant slots are contiguous from index 0 (one per chosen
    // participant + a trailing "add" slot); scan until the first absent slot
    // and collect the non-empty, de-duplicated picks.
    for (let i = 0; ; i++) {
      const el = getEl(participantId(i));
      if (el === null) break;
      const p = el.value.trim();
      if (p !== '' && !participants.includes(p)) participants.push(p);
    }
    if (participants.length > 0) value['participants'] = participants;

    const durationDaysRaw = getVal(durationDaysId);
    if (durationDaysRaw !== '') {
      const days = Number(durationDaysRaw);
      if (!Number.isInteger(days) || days < 0) {
        send(host.invalidFact(entityId, predicate, t('fact.recurrence.invalid_duration')));
        return;
      }
      value['duration'] = { days };
    }

    const result = validateValueWithDeclaration(decl, value);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  const onAnchorKindChange = (): void => {
    syncAnchorVisibility();
    commit();
  };
  const onStepCycleChange = (i: number) => (): void => {
    syncInstanceOptions(i);
    commit();
  };
  const onLabeledCycleChange = (): void => {
    syncLabelOptions();
    commit();
  };

  return div(
    {
      class: 'fact-recurrence',
      id: fieldId,
      role: 'group',
      'data-value-kind': 'recurrence_rule',
      'aria-label': predicateLabel(predicate),
    },
    [
      each(
        state.map((s) => {
          const digest = structuredDigest(s, entityId, predicate);
          return [`${digest}:${Object.keys(s.entities).length}`] as const;
        }),
        {
          key: (k) => k,
          render: (): Renderable => {
            const snap = state.peek();
            const calendar = host.activeCalendar(snap);
            const cur = readStructuredValue(snap, entityId, predicate) ?? {};
            const cycleNames = recurrenceCycleNames(calendar);

            const curCycle = typeof cur['cycle'] === 'string' ? (cur['cycle'] as string) : '';
            const curEveryN = typeof cur['every_n'] === 'number' ? String(cur['every_n']) : '1';
            const anchor =
              cur['anchor'] && typeof cur['anchor'] === 'object'
                ? (cur['anchor'] as Record<string, unknown>)
                : null;
            const curAnchorKind =
              anchor && typeof anchor['kind'] === 'string'
                ? (anchor['kind'] as string)
                : 'day_of_cycle';
            const curOffset =
              curAnchorKind === 'day_of_cycle' && anchor && typeof anchor['offset'] === 'number'
                ? String(anchor['offset'])
                : '';
            const curPath =
              curAnchorKind === 'nested_position' && Array.isArray(anchor?.['path'])
                ? (anchor!['path'] as { cycle?: unknown; instance?: unknown }[])
                : [];
            const curLabeledCycle =
              curAnchorKind === 'labeled' && anchor && typeof anchor['cycle'] === 'string'
                ? (anchor['cycle'] as string)
                : '';
            const curLabel =
              curAnchorKind === 'labeled' && anchor && typeof anchor['label'] === 'string'
                ? (anchor['label'] as string)
                : '';
            const curAtLocation =
              typeof cur['at_location'] === 'string' ? (cur['at_location'] as string) : '';
            const curParticipants = Array.isArray(cur['participants'])
              ? (cur['participants'] as unknown[]).filter(
                  (p): p is string => typeof p === 'string',
                )
              : [];
            const curDurationDays =
              cur['duration'] &&
              typeof cur['duration'] === 'object' &&
              typeof (cur['duration'] as Record<string, unknown>)['days'] === 'number'
                ? String((cur['duration'] as Record<string, unknown>)['days'])
                : '';

            const cycleOptions = (selected: string): Mountable[] => [
              option({ value: '' }, [text(t('fact.recurrence.cycle_placeholder'))]),
              ...cycleNames.map((name) =>
                option(
                  name === selected ? { value: name, selected: 'selected' } : { value: name },
                  [text(name)],
                ),
              ),
            ];

            const labelBearingCycles = recurrenceLabeledCycleNames(calendar);
            const locationOptions = refOptions(snap, entityId, `${predicate}#at_location`, [
              'location',
            ]);
            // Participant candidates: any non-self, non-deleted entity.
            const participantCandidates = refOptions(
              snap,
              entityId,
              `${predicate}#participants`,
              [],
            );

            const rows: Mountable[] = [
              label({ class: 'fact-recurrence-field' }, [
                span({ class: 'fact-recurrence-lbl' }, [text(t('fact.recurrence.cycle'))]),
                select(
                  {
                    id: cycleId,
                    class: 'dl-select dl-select--full',
                    value: curCycle,
                    'data-testid': `fact-${predicate}-cycle`,
                    onChange: commit,
                  },
                  cycleOptions(curCycle),
                ),
              ]),
              label({ class: 'fact-recurrence-field' }, [
                span({ class: 'fact-recurrence-lbl' }, [text(t('fact.recurrence.every_n'))]),
                input({
                  type: 'number',
                  id: everyNId,
                  class: 'fact-recurrence-num',
                  min: '1',
                  step: '1',
                  value: curEveryN,
                  'data-testid': `fact-${predicate}-every-n`,
                  onChange: commit,
                }),
              ]),
              label({ class: 'fact-recurrence-field' }, [
                span({ class: 'fact-recurrence-lbl' }, [text(t('fact.recurrence.anchor'))]),
                select(
                  {
                    id: anchorKindId,
                    class: 'dl-select',
                    value: curAnchorKind,
                    'data-testid': `fact-${predicate}-anchor-kind`,
                    onChange: onAnchorKindChange,
                  },
                  [
                    option(
                      curAnchorKind === 'day_of_cycle'
                        ? { value: 'day_of_cycle', selected: 'selected' }
                        : { value: 'day_of_cycle' },
                      [text(t('fact.recurrence.anchor_day_of_cycle'))],
                    ),
                    option(
                      curAnchorKind === 'nested_position'
                        ? { value: 'nested_position', selected: 'selected' }
                        : { value: 'nested_position' },
                      [text(t('fact.recurrence.anchor_nested_position'))],
                    ),
                    option(
                      curAnchorKind === 'labeled'
                        ? { value: 'labeled', selected: 'selected' }
                        : { value: 'labeled' },
                      [text(t('fact.recurrence.anchor_labeled'))],
                    ),
                  ],
                ),
              ]),
            ];

            // ── anchor-specific sub-inputs (all three groups always present;
            //    inactive ones are `hidden` so the GM can switch freely before
            //    a value is stored and the keyed `each` need not reseed) ──
            const hiddenUnless = (kind: string): Record<string, string> =>
              kind === curAnchorKind ? {} : { hidden: 'hidden' };

            // day_of_cycle
            rows.push(
              div({ id: groupId('day_of_cycle'), class: 'fact-recurrence-anchor', ...hiddenUnless('day_of_cycle') }, [
                label({ class: 'fact-recurrence-field' }, [
                  span({ class: 'fact-recurrence-lbl' }, [text(t('fact.recurrence.offset'))]),
                  input({
                    type: 'number',
                    id: offsetId,
                    class: 'fact-recurrence-num',
                    step: '1',
                    value: curOffset,
                    placeholder: t('fact.recurrence.offset_placeholder'),
                    'data-testid': `fact-${predicate}-offset`,
                    onChange: commit,
                  }),
                ]),
              ]),
            );

            // nested_position
            const npStepNodes: Mountable[] = [];
            for (let i = 0; i < NP_STEPS; i++) {
              const step = curPath[i];
              const stepCycle = step && typeof step.cycle === 'string' ? (step.cycle as string) : '';
              const stepInstance =
                step && (typeof step.instance === 'string' || typeof step.instance === 'number')
                  ? String(step.instance)
                  : '';
              const instanceNames = recurrenceInstanceNames(calendar, stepCycle);
              npStepNodes.push(
                div({ class: 'fact-recurrence-step' }, [
                  label({ class: 'fact-recurrence-field' }, [
                    span({ class: 'fact-recurrence-lbl' }, [text(t('fact.recurrence.path_cycle'))]),
                    select(
                      {
                        id: npCycleId(i),
                        class: 'dl-select',
                        value: stepCycle,
                        'data-testid': `fact-${predicate}-np-cycle-${i}`,
                        onChange: onStepCycleChange(i),
                      },
                      cycleOptions(stepCycle),
                    ),
                  ]),
                  label({ class: 'fact-recurrence-field' }, [
                    span({ class: 'fact-recurrence-lbl' }, [
                      text(t('fact.recurrence.path_instance')),
                    ]),
                    // The instance picker is repopulated imperatively when the
                    // step cycle changes (`syncInstanceOptions`); the initial
                    // render seeds it from the stored cycle. A cycle with named
                    // instances ⇒ a closed select; otherwise an ordinal input.
                    div({ id: `${npInstanceId(i)}-wrap`, class: 'fact-recurrence-instance-wrap' }, [
                      instanceNames.length > 0
                        ? select(
                            {
                              id: npInstanceId(i),
                              class: 'dl-select',
                              value: stepInstance,
                              'data-testid': `fact-${predicate}-np-instance-${i}`,
                              onChange: commit,
                            },
                            [
                              option({ value: '' }, [
                                text(t('fact.recurrence.instance_placeholder')),
                              ]),
                              ...instanceNames.map((n) =>
                                option(
                                  n === stepInstance
                                    ? { value: n, selected: 'selected' }
                                    : { value: n },
                                  [text(n)],
                                ),
                              ),
                            ],
                          )
                        : input({
                            type: 'number',
                            id: npInstanceId(i),
                            class: 'fact-recurrence-num',
                            step: '1',
                            value: stepInstance,
                            placeholder: t('fact.recurrence.ordinal_placeholder'),
                            'data-testid': `fact-${predicate}-np-instance-${i}`,
                            onChange: commit,
                          }),
                    ]),
                  ]),
                ]),
              );
            }
            rows.push(
              div(
                { id: groupId('nested_position'), class: 'fact-recurrence-anchor', ...hiddenUnless('nested_position') },
                npStepNodes,
              ),
            );

            // labeled
            rows.push(
              div({ id: groupId('labeled'), class: 'fact-recurrence-anchor', ...hiddenUnless('labeled') }, [
                label({ class: 'fact-recurrence-field' }, [
                  span({ class: 'fact-recurrence-lbl' }, [text(t('fact.recurrence.labeled_cycle'))]),
                  select(
                    {
                      id: labeledCycleId,
                      class: 'dl-select',
                      value: curLabeledCycle,
                      'data-testid': `fact-${predicate}-labeled-cycle`,
                      onChange: onLabeledCycleChange,
                    },
                    [
                      option({ value: '' }, [text(t('fact.recurrence.cycle_placeholder'))]),
                      ...labelBearingCycles.map((name) =>
                        option(
                          name === curLabeledCycle
                            ? { value: name, selected: 'selected' }
                            : { value: name },
                          [text(name)],
                        ),
                      ),
                    ],
                  ),
                ]),
                label({ class: 'fact-recurrence-field' }, [
                  span({ class: 'fact-recurrence-lbl' }, [text(t('fact.recurrence.label'))]),
                  select(
                    {
                      id: labelId,
                      class: 'dl-select',
                      value: curLabel,
                      'data-testid': `fact-${predicate}-label`,
                      onChange: commit,
                    },
                    [
                      option({ value: '' }, [text(t('fact.recurrence.label_placeholder'))]),
                      ...recurrenceCycleLabels(calendar, curLabeledCycle).map((lbl) =>
                        option(
                          lbl === curLabel ? { value: lbl, selected: 'selected' } : { value: lbl },
                          [text(lbl)],
                        ),
                      ),
                    ],
                  ),
                ]),
              ]),
            );

            // ── optional defaults: at_location / participants / duration ──
            rows.push(
              label({ class: 'fact-recurrence-field' }, [
                span({ class: 'fact-recurrence-lbl' }, [text(t('fact.recurrence.at_location'))]),
                select(
                  {
                    id: atLocationId,
                    class: 'dl-select dl-select--full',
                    value: curAtLocation,
                    'data-testid': `fact-${predicate}-at-location`,
                    onChange: commit,
                  },
                  [
                    option({ value: '' }, [text(t('fact.recurrence.at_location_placeholder'))]),
                    ...locationOptions.map((o) =>
                      option(
                        o.id === curAtLocation
                          ? { value: o.id, selected: 'selected' }
                          : { value: o.id },
                        [text(o.name)],
                      ),
                    ),
                  ],
                ),
              ]),
            );

            // Participants: one select per chosen participant + a trailing
            // empty "add" slot. Clearing a row to the blank option removes it.
            const participantSlots = [...curParticipants, ''];
            rows.push(
              div(
                {
                  class: 'fact-recurrence-participants',
                  role: 'group',
                  'aria-label': t('fact.recurrence.participants'),
                },
                [
                  span({ class: 'fact-recurrence-lbl' }, [text(t('fact.recurrence.participants'))]),
                  ...participantSlots.map((chosen, i) =>
                    select(
                      {
                        id: participantId(i),
                        class: 'dl-select dl-select--full',
                        value: chosen,
                        'data-testid': `fact-${predicate}-participant-${i}`,
                        onChange: commit,
                      },
                      [
                        option({ value: '' }, [
                          text(t('fact.recurrence.participant_placeholder')),
                        ]),
                        ...participantCandidates.map((o) =>
                          option(
                            o.id === chosen ? { value: o.id, selected: 'selected' } : { value: o.id },
                            [text(o.name)],
                          ),
                        ),
                      ],
                    ),
                  ),
                ],
              ),
            );

            rows.push(
              label({ class: 'fact-recurrence-field' }, [
                span({ class: 'fact-recurrence-lbl' }, [text(t('fact.recurrence.duration_days'))]),
                input({
                  type: 'number',
                  id: durationDaysId,
                  class: 'fact-recurrence-num',
                  min: '0',
                  step: '1',
                  value: curDurationDays,
                  placeholder: t('fact.recurrence.duration_days_placeholder'),
                  'data-testid': `fact-${predicate}-duration-days`,
                  onChange: commit,
                }),
              ]),
            );

            return rows;
          },
        },
      ),
    ],
  );
}

// ─── single-input renderers ────────────────────────────────────────

function viewTextLikeInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  kind: ValueKind,
  host: FactRowHost<Model, Msg>,
): Mountable {
  // Save commits ONLY on Enter or on the explicit Save button (rendered
  // by `viewSaveButton`). Blur intentionally does NOT commit — the
  // dirty state stays visible so the GM knows they have unsaved work.
  // The Save button reads the current input value via `document.getElementById`
  // at click time so we don't have to thread the raw input through state.
  const predicate = decl.id;
  const placeholder = predicatePlaceholder(predicate);
  const rowKey = factRowKey(entityId, predicate);

  const commit = (raw: string): void => {
    if (raw === '') {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    const result = validateValueWithDeclaration(decl, raw);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  return textInput({
    // Dirty-buffer accessor: when the user has typed since the last
    // successful save, return what they typed (`inputBuffer[key]`).
    // Otherwise fall back to the persisted fact value. This makes the
    // controlled-input contract race-free — the DOM `el.value` is no
    // longer a side channel the binding can clobber across async
    // ACKs or entity re-folds. See the 2026-05-27 thread + the
    // canonical pattern in fact-row-state.ts.
    value: state.map((s) => {
      const buffered = inputBufferAtKey(s, rowKey);
      if (buffered !== undefined) return buffered;
      const v = currentValue(factAtPredicate(s, entityId, predicate));
      return v === undefined || v === null ? '' : String(v);
    }),
    placeholder,
    testid: `fact-${predicate}`,
    ariaLabel: predicateLabel(predicate),
    onInput: (value: string) => {
      // Fold the live DOM value into `inputBuffer[key]` so subsequent
      // commits keep the binding's accessor returning what the user
      // typed. The reducer also bumps the dirty epoch + sets the
      // dirty flag — all in one Msg.
      send(host.markDirty(entityId, predicate, value));
    },
    attrs: {
      name: predicate,
      id: fieldId,
      'data-value-kind': kind,
      'aria-describedby': `${fieldId}-error`,
      onFocus: () => send(host.focusFact(entityId, predicate)),
      onBlur: () => {
        // No commit on blur — the dirty state intentionally stays visible
        // so the GM knows the value isn't yet persisted.
        send(host.blurFact(entityId, predicate));
      },
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          commit((event.target as HTMLInputElement).value);
        }
      },
    },
  });
}

function viewRichTextInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  // WYSIWYG markdown editor (ADR-0002). The stored value is always markdown;
  // the user never sees raw markup. Backed by @llui/markdown-editor — a
  // Lexical WYSIWYG widget mounted as a SUB-APP (`mountApp`) into the container
  // the eager fact-row builder returns. We mount in `onMount` (after the
  // container is attached — Lexical needs a connected contenteditable).
  // `rich_text_mode` maps to readonly (`none`) / toolbar (`full`). The shared
  // Save button does NOT read this composite editor; the row self-commits on
  // blur (the historical `commit`-on-blur contract).
  const predicate = decl.id;
  const mode = decl.rich_text_mode ?? 'full';
  const initial = currentValue(factAtPredicate(state.peek(), entityId, predicate));
  // Latest markdown the editor has emitted (debounced onChange); committed on
  // blur. Seeded from the render-time snapshot — external value changes (AI
  // draft landing, lossless reject) re-mount the row through the value-kind
  // scope, recreating this node with the fresh seed.
  let latest = initial === undefined || initial === null ? '' : String(initial);

  // The mount adapter (`<dl-llui-md-editor>`) mounts @llui/markdown-editor on
  // attach. Create it imperatively + return the real node (eager fact-row
  // builder mounts a real child directly); the editor lands inside it.
  const node = document.createElement(MD_EDITOR_TAG);
  node.id = fieldId;
  node.setAttribute('data-testid', `fact-${predicate}`);
  node.setAttribute('data-value-kind', 'rich_text');
  node.setAttribute('mode', mode);
  node.setAttribute('value', latest);
  node.setAttribute('placeholder', predicatePlaceholder(predicate));
  node.setAttribute('aria-label', predicateLabel(predicate));
  node.setAttribute('aria-describedby', `${fieldId}-error`);
  // The editor emits `md-change` (debounced markdown); track it + mark dirty.
  node.addEventListener('md-change', (e: Event) => {
    latest = (e as CustomEvent<{ value: string }>).detail.value;
    send(host.markDirty(entityId, predicate));
  });
  node.addEventListener('focusin', () => send(host.focusFact(entityId, predicate)));
  // Commit on blur (the historical `commit` contract — the shared Save button
  // doesn't read this composite editor); ignore focus moves WITHIN the editor.
  node.addEventListener('focusout', (e: Event) => {
    if (node.contains((e as FocusEvent).relatedTarget as Node | null)) return;
    if (latest === '') send(host.softDeleteFact(entityId, predicate));
    else send(host.editFact(entityId, predicate, latest));
  });
  return mountable(() => node);
}

function viewNumberInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  const placeholder = predicatePlaceholder(predicate);
  const units = unitsForPredicate(decl);
  const unitId = `${fieldId}-unit`;
  const rowKey = factRowKey(entityId, predicate);

  // A number value is either a bare `number` (unitless) or a dimensioned
  // `{ canonical_value, unit_id }` (ADR-0007): the fact stores its dimension's
  // canonical base value, and the input shows it rendered back into the
  // authoring/display unit. Read both parts for the controlled inputs.
  const readNum = (s: Model): string => {
    const buffered = inputBufferAtKey(s, rowKey);
    if (buffered !== undefined) return buffered;
    const nv = readNumberValue(currentValue(factAtPredicate(s, entityId, predicate)));
    if (!nv) return '';
    if (nv.kind === 'plain') return String(nv.value);
    const unit = campaignUnitRegistry().unit(nv.unit_id);
    if (!unit) return ''; // unknown unit — can't render the canonical value
    return decimalToString(fromCanonicalValue(nv.canonical_value, unit));
  };
  const readUnit = (s: Model): string => {
    const nv = readNumberValue(currentValue(factAtPredicate(s, entityId, predicate)));
    if (nv && nv.kind === 'dimensioned') return nv.unit_id;
    return decl.unit ?? '';
  };

  // Commit the (number, unit) pair. No units declared / no unit chosen →
  // write a bare number; a unit chosen → store the dimension's canonical base
  // value (computed exactly via quantity.ts) with the unit as provenance.
  const commit = (raw: string, unit: string): void => {
    if (raw === '') {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      send(host.invalidFact(entityId, predicate, t('entity.invalid_number')));
      return;
    }
    let value: number | { canonical_value: string; unit_id: string };
    if (units.length > 0 && unit) {
      const unitDef = campaignUnitRegistry().unit(unit);
      if (!unitDef) {
        send(host.invalidFact(entityId, predicate, t('entity.invalid_number')));
        return;
      }
      value = { canonical_value: toCanonicalValue(raw.trim(), unitDef), unit_id: unit };
    } else {
      value = n;
    }
    const result = validateValueWithDeclaration(decl, value);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  const numberInput = textInput({
    type: 'number',
    value: state.map((s) => readNum(s)),
    placeholder,
    testid: `fact-${predicate}`,
    ariaLabel: predicateLabel(predicate),
    onInput: (value: string) => {
      send(host.markDirty(entityId, predicate, value));
    },
    attrs: {
      name: predicate,
      id: fieldId,
      'data-value-kind': 'number',
      'aria-describedby': `${fieldId}-error`,
      onFocus: () => send(host.focusFact(entityId, predicate)),
      onBlur: () => {
        // No commit on blur — dirty state stays visible.
        send(host.blurFact(entityId, predicate));
      },
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          const u =
            (document.getElementById(unitId) as HTMLSelectElement | null)?.value ?? '';
          commit((event.target as HTMLInputElement).value, u);
        }
      },
    },
  });

  if (units.length === 0) return numberInput;

  // Unit picker beside the number. Changing the unit commits immediately
  // (pairing with the current number from the DOM). The number commits on
  // Enter / Save as usual.
  const unitSelect = select(
    {
      id: unitId,
      class: 'dl-select fact-number-unit',
      'data-testid': `fact-${predicate}-unit`,
      'aria-label': `${predicateLabel(predicate)} unit`,
      value: state.map((s) => readUnit(s)),
      onChange: (event: Event) => {
        const unit = (event.target as HTMLSelectElement).value;
        const numEl = document.getElementById(fieldId) as HTMLInputElement | null;
        send(host.markDirty(entityId, predicate));
        commit(numEl?.value ?? '', unit);
      },
    },
    ((): Mountable[] => {
      const opts: Mountable[] = [option({ value: '' }, [t('entity.unit.none')])];
      for (const u of units) {
        opts.push(option({ value: u.id }, [resolveLocalizedString(u.symbol)]));
      }
      return opts;
    })(),
  );

  return div({ class: 'fact-number' }, [numberInput, unitSelect]);
}

// ─── new value-kind renderers (ADR-0002) ───────────────────────────

/**
 * Real-world date-only / time-only input. `mode` picks the native control
 * (`date` → "YYYY-MM-DD"; `time` → "HH:MM"). The stored value is the raw
 * input string, which `RealDateSchema` / `RealTimeSchema` accept directly
 * (no timezone — date/time-only kinds carry no offset).
 */
function viewDateTimeLikeInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  mode: 'date' | 'time',
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  const kind = mode === 'date' ? 'real_date' : 'real_time';

  const commit = (raw: string): void => {
    if (raw === '') {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    const result = validateValueWithDeclaration(decl, raw);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  return input({
    type: mode,
    name: predicate,
    id: fieldId,
    value: state.map((s) => {
      const v = currentValue(factAtPredicate(s, entityId, predicate));
      return typeof v === 'string' ? v : '';
    }),
    'data-testid': `fact-${predicate}`,
    'data-value-kind': kind,
    'aria-label': predicateLabel(predicate),
    'aria-describedby': `${fieldId}-error`,
    onFocus: () => send(host.focusFact(entityId, predicate)),
    onBlur: () => send(host.blurFact(entityId, predicate)),
    onChange: (event: Event) => {
      const raw = (event.target as HTMLInputElement).value;
      send(host.markDirty(entityId, predicate));
      commit(raw);
    },
  });
}

/**
 * Render a calendar-grid `dateField` bound to the host's single open-picker
 * slice (`Model.datePicker`). Every in-world day picker in the editor (game
 * dates, validity bounds) funnels through here so the keyed-`each`-remount,
 * slice plumbing, and i18n catalog live in one place. The `id` namespaces
 * which field owns the open grid; the keyed `each` digests the value plus
 * the live view-state so the build-once atom remounts on navigation.
 */
function gridDateField<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  host: FactRowHost<Model, Msg>,
  opts: {
    id: string;
    testid: string;
    clearable: boolean;
    valueOf: (s: Model) => number | null;
    onSelect: (day: number) => void;
    onClear?: () => void;
    /** Selection granularity (ADR-0005 §5); forwarded to the grid. */
    granularity?: CalendarGranularity;
    /** Granularity levels to offer in the grid's in-place segmented control. */
    granularities?: readonly CalendarGranularity[];
    /** The user switched granularity in the grid. */
    onGranularity?: (granularity: CalendarGranularity) => void;
    /** Label for the grid's leading "none" segment (clears the value). */
    granularityClearLabel?: string;
    /** The user picked the "none" segment. */
    onGranularityClear?: () => void;
    /** Trigger text when the value is null (overrides the catalog placeholder). */
    placeholder?: string;
  },
): Mountable {
  const { id } = opts;
  // `gridDateField` is rendered inside nested `each` rows (the fact-value
  // renderer, and the validity editor's bound chips). A bare `state.map`
  // built inside a row template rebases to the row ctx `{item, state, index}`
  // instead of the Model, so the slice read would be `undefined` and the grid
  // would never open. Wrapping in `derived([state], s => s)` rebases to
  // `ctx.state` (the Model) — the row-aware accessor pattern (see this file's
  // `viewFactRowFor`).
  const model = derived([state], (s) => s);
  return each(
    model.map((s) => {
      const dp = host.datePicker(s);
      const open = dp !== null && dp.id === id;
      const day = opts.valueOf(s);
      const view = open
        ? `${dp.cursor.eraId}|${dp.cursor.year}|${dp.cursor.monthIndex}|${dp.overlay}`
        : 'closed';
      return [`${String(day ?? '')}:${view}`] as const;
    }),
    {
      key: (k) => k,
      render: () => {
        const snapshot = model.peek();
        const cal = host.activeCalendar(snapshot);
        const day = opts.valueOf(snapshot);
        const dp = host.datePicker(snapshot);
        const open = dp !== null && dp.id === id;
        return [
          dateField({
            calendar: cal,
            value: day,
            open,
            ...(open ? { cursor: dp.cursor, overlay: dp.overlay } : {}),
            clearable: opts.clearable,
            id,
            testid: opts.testid,
            locale: 'en',
            messages: {
              ...host.datePickerMessages(),
              ...(opts.placeholder !== undefined ? { placeholder: opts.placeholder } : {}),
            },
            onOpen: (seed) => send(host.openDatePicker(id, seed)),
            onClose: () => send(host.closeDatePicker()),
            onNavigate: (next) => send(host.navigateDatePicker(next)),
            onOverlay: (next) => send(host.overlayDatePicker(next)),
            onSelect: opts.onSelect,
            ...(opts.onClear !== undefined ? { onClear: opts.onClear } : {}),
            ...(opts.granularity !== undefined ? { granularity: opts.granularity } : {}),
            ...(opts.granularities !== undefined ? { granularities: opts.granularities } : {}),
            ...(opts.onGranularity !== undefined ? { onGranularity: opts.onGranularity } : {}),
            ...(opts.granularityClearLabel !== undefined
              ? { granularityClearLabel: opts.granularityClearLabel }
              : {}),
            ...(opts.onGranularityClear !== undefined
              ? { onGranularityClear: opts.onGranularityClear }
              : {}),
          }),
        ];
      },
    },
  );
}

/**
 * In-world (game) date/datetime/time input. The stored value is the
 * canonical-day-count `WorldTime` record (`{ day }`, `{ day,
 * seconds_into_day }`, or `{ seconds_into_day }`). The day axis uses the
 * calendar-aware `dateField` grid; the time-of-day axis uses a native time
 * control mapped to `seconds_into_day`.
 */
function viewGameTimeInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  kind: ValueKind,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  const hasDay = kind === 'game_date' || kind === 'game_datetime';
  const hasTime = kind === 'game_datetime' || kind === 'game_time';
  const timeId = `${fieldId}-time`;

  // Read the stored WorldTime parts. `day` is a canonical-day-count integer
  // (what calendarInput takes/returns); `seconds_into_day` drives the time
  // input.
  const readDay = (s: Model): number | null => {
    const v = currentValue(factAtPredicate(s, entityId, predicate));
    if (v && typeof v === 'object' && typeof (v as { day?: unknown }).day === 'number') {
      return (v as { day: number }).day;
    }
    return null;
  };
  const readSeconds = (s: Model): number | null => {
    const v = currentValue(factAtPredicate(s, entityId, predicate));
    const secs =
      v && typeof v === 'object' ? (v as { seconds_into_day?: unknown }).seconds_into_day : undefined;
    return typeof secs === 'number' ? secs : null;
  };
  const readTimeStr = (s: Model): string => {
    const secs = readSeconds(s);
    if (secs === null) return '';
    const hh = String(Math.floor(secs / 3600)).padStart(2, '0');
    const mm = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  // Commit the combined WorldTime from a (day, time-string) pair. Either
  // part may be null/'' — when everything required is empty, soft-delete.
  const commit = (day: number | null, timeStr: string): void => {
    const dayEmpty = !hasDay || day === null;
    const timeEmpty = !hasTime || timeStr === '';
    if ((!hasTime && dayEmpty) || (!hasDay && timeEmpty) || (hasDay && hasTime && dayEmpty && timeEmpty)) {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    const value: Record<string, number> = {};
    if (hasDay && day !== null) value['day'] = day;
    if (hasTime && timeStr !== '') {
      const [hh, mm] = timeStr.split(':');
      value['seconds_into_day'] = (Number(hh) || 0) * 3600 + (Number(mm) || 0) * 60;
    }
    const result = validateValueWithDeclaration(decl, value);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  const children: Mountable[] = [];

  // Calendar-aware day picker (game_date / game_datetime). The `dateField`
  // grid (era/year/month grid, keyboard nav, today ring) replaces the old
  // four-select calendarInput. Its transient view-state lives in the single
  // app-wide `Model.datePicker` slice under id `pickerId`; the keyed `each`
  // digests the stored day plus that view-state so the build-once atom
  // remounts on navigation. `value` speaks canonical-day-count — exactly the
  // WorldTime `day` we store.
  const pickerId = `fact:${entityId}:${predicate}:day`;
  const commitDayWithTime = (next: number | null): void => {
    send(host.markDirty(entityId, predicate));
    const timeEl = document.getElementById(timeId) as HTMLInputElement | null;
    commit(next, timeEl?.value ?? '');
  };
  if (hasDay) {
    children.push(
      div({ class: 'fact-game-day', 'data-testid': `fact-${predicate}-day` }, [
        gridDateField(state, send, host, {
          id: pickerId,
          testid: `fact-${predicate}-cal`,
          clearable: true,
          valueOf: (s) => readDay(s),
          onSelect: (next) => commitDayWithTime(next),
          onClear: () => commitDayWithTime(null),
        }),
      ]),
    );
  }

  // Time-of-day input (game_datetime / game_time). Wrapped in a keyed `each`
  // over the stored day so the change handler closes over the captured
  // `day` — a one-shot snapshot at render time (the calendar picker commits
  // the day independently, and event handlers run after mount).
  if (hasTime) {
    children.push(
      div(
        { class: 'fact-game-time-wrap' },
        [
          each(state.map((s) => [String(readDay(s) ?? '')] as const), {
            key: (k) => k,
            render: () => {
              const capturedDay = readDay(state.peek());
              return [
                input({
                  type: 'time',
                  id: timeId,
                  class: 'fact-game-time',
                  value: state.map((s) => readTimeStr(s)),
                  'data-testid': `fact-${predicate}-time`,
                  'data-value-kind': kind,
                  'aria-label': `${predicateLabel(predicate)} time`,
                  onFocus: () => send(host.focusFact(entityId, predicate)),
                  onBlur: () => send(host.blurFact(entityId, predicate)),
                  onChange: (event: Event) => {
                    const time = (event.target as HTMLInputElement).value;
                    send(host.markDirty(entityId, predicate));
                    // Combine with the day captured at render time (the
                    // calendar picker commits the day independently).
                    commit(capturedDay, time);
                  },
                }),
              ];
            },
          }),
        ],
      ),
    );
  }
  return div({ class: 'fact-game-time-field', id: fieldId }, children);
}

/**
 * In-world RANGE input (`game_date_range` / `game_datetime_range`, #221): a
 * from→to pair of calendar-aware day pickers (plus a per-bound time input for
 * the datetime range). Every pick yields a COMPLETE, valid `{ from, to }` —
 * setting only one bound makes a degenerate single-point span (`to == from`)
 * the user then extends; a bound set out of order auto-clamps the partner so
 * `to >= from`; clearing either bound removes the whole range. The wall-clock
 * sibling (`viewDatetimeRangeInput`) can read a transient partial off the DOM;
 * the calendar grid commits through the Model, so the degenerate-span approach
 * avoids ever holding an unrepresentable partial range.
 */
function viewGameTimeRangeInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  kind: ValueKind,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  const hasTime = kind === 'game_datetime_range';
  type Side = 'from' | 'to';
  interface Bound {
    day: number;
    seconds_into_day?: number;
  }

  const readBound = (s: Model, side: Side): Bound | null => {
    const v = currentValue(factAtPredicate(s, entityId, predicate));
    const b = v && typeof v === 'object' ? (v as Record<string, unknown>)[side] : undefined;
    if (b && typeof b === 'object' && typeof (b as { day?: unknown }).day === 'number') {
      const day = (b as { day: number }).day;
      const secs = (b as { seconds_into_day?: unknown }).seconds_into_day;
      return hasTime ? { day, seconds_into_day: typeof secs === 'number' ? secs : 0 } : { day };
    }
    return null;
  };
  const readDay = (s: Model, side: Side): number | null => readBound(s, side)?.day ?? null;
  const readTimeStr = (s: Model, side: Side): string => {
    const secs = readBound(s, side)?.seconds_into_day;
    if (typeof secs !== 'number') return '';
    return `${String(Math.floor(secs / 3600)).padStart(2, '0')}:${String(
      Math.floor((secs % 3600) / 60),
    ).padStart(2, '0')}`;
  };
  const mk = (day: number, secs: number): Bound =>
    hasTime ? { day, seconds_into_day: secs } : { day };
  const secsOf = (timeStr: string): number => {
    if (timeStr === '') return 0;
    const [hh, mm] = timeStr.split(':');
    return (Number(hh) || 0) * 3600 + (Number(mm) || 0) * 60;
  };
  const afterOrEqual = (a: Bound, b: Bound): boolean =>
    a.day > b.day || (a.day === b.day && (a.seconds_into_day ?? 0) >= (b.seconds_into_day ?? 0));

  // Recommit a complete {from,to} after the `side` bound changed, clamping the
  // partner so `to >= from`.
  const commitSide = (side: Side, bound: Bound): void => {
    send(host.markDirty(entityId, predicate));
    let from: Bound;
    let to: Bound;
    if (side === 'from') {
      from = bound;
      to = readBound(state.peek(), 'to') ?? { ...bound };
      if (!afterOrEqual(to, from)) to = { ...from };
    } else {
      to = bound;
      from = readBound(state.peek(), 'from') ?? { ...bound };
      if (!afterOrEqual(to, from)) from = { ...to };
    }
    const result = validateValueWithDeclaration(decl, { from, to });
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  const boundField = (side: Side, labelKey: string): Mountable => {
    const children: Mountable[] = [
      label({ class: 'fact-sub-label' }, [text(t(labelKey))]),
      div({ class: 'fact-game-day', 'data-testid': `fact-${predicate}-${side}-day` }, [
        gridDateField(state, send, host, {
          id: `fact:${entityId}:${predicate}:${side}:day`,
          testid: `fact-${predicate}-${side}-cal`,
          clearable: true,
          valueOf: (s) => readDay(s, side),
          onSelect: (next) => {
            if (next === null) {
              send(host.softDeleteFact(entityId, predicate));
              return;
            }
            commitSide(side, mk(next, secsOf(readTimeStr(state.peek(), side))));
          },
          onClear: () => send(host.softDeleteFact(entityId, predicate)),
        }),
      ]),
    ];
    if (hasTime) {
      children.push(
        div({ class: 'fact-game-time-wrap' }, [
          each(state.map((s) => [String(readDay(s, side) ?? '')] as const), {
            key: (k) => k,
            render: () => {
              const capturedDay = readDay(state.peek(), side);
              return [
                input({
                  type: 'time',
                  class: 'fact-game-time',
                  value: state.map((s) => readTimeStr(s, side)),
                  'data-testid': `fact-${predicate}-${side}-time`,
                  'aria-label': `${predicateLabel(predicate)} ${side}`,
                  onFocus: () => send(host.focusFact(entityId, predicate)),
                  onBlur: () => send(host.blurFact(entityId, predicate)),
                  onChange: (event: Event) => {
                    if (capturedDay === null) return;
                    commitSide(
                      side,
                      mk(capturedDay, secsOf((event.target as HTMLInputElement).value)),
                    );
                  },
                }),
              ];
            },
          }),
        ]),
      );
    }
    return div({ class: 'fact-range-bound' }, children);
  };

  return div({ class: 'fact-game-range-field', id: fieldId }, [
    boundField('from', 'validity.from'),
    boundField('to', 'validity.to'),
  ]);
}

/**
 * Tuple input — one fact whose value is a named-field record (ADR-0002).
 * Each declared `tuple_field` renders its own sub-control; the record
 * commits as a unit. Used for role-qualified entries like a Government's
 * `{role, individual, notes?}`. (Repeatability — many such records, each
 * its own validity — is the `repeatable` predicate dimension, handled by
 * the grouped renderer, not here.)
 */
function viewTupleInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  const fields = decl.tuple_fields ?? [];

  // Tuple records are keyed by the field's opaque `fld_` id (ADR-0004 §1),
  // not its display name — so the stored record is rename-stable.
  const subId = (fid: string): string => `${fieldId}-tf-${fid}`;

  const commit = (): void => {
    const record: Record<string, unknown> = {};
    for (const f of fields) {
      const el = document.getElementById(subId(f.id)) as
        | HTMLInputElement
        | HTMLSelectElement
        | null;
      const raw = el?.value ?? '';
      if (raw === '') continue;
      record[f.id] = f.value_kind === 'number' ? Number(raw) : raw;
    }
    if (Object.keys(record).length === 0) {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    const result = validateValueWithDeclaration(decl, record);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  return div(
    { class: 'fact-tuple', id: fieldId, role: 'group', 'aria-label': predicateLabel(predicate) },
    [
      each(state.map((s) => [`${Object.keys(s.entities).length}`] as const), {
        key: (k) => k,
        render: () => {
          const snapshot = state.peek();
          const currentVal = currentValue(factAtPredicate(snapshot, entityId, predicate));
          const current =
            currentVal && typeof currentVal === 'object'
              ? (currentVal as Record<string, unknown>)
              : {};
          const refCandidatesByField = new Map<string, RefOption[]>();
          for (const f of fields) {
            if (f.value_kind === 'ref') {
              const roots = (f.ref_roots ?? []) as EntityRoot[];
              const allow = new Set<string>(roots);
              const out: RefOption[] = [];
              for (const [id, entity] of Object.entries(snapshot.entities)) {
                if (id === entityId || entity.soft_deleted) continue;
                if (allow.size > 0 && !allow.has(entity.root)) continue;
                out.push({ id, name: refEntityName(entity) });
              }
              out.sort((a, b) => a.name.localeCompare(b.name));
              refCandidatesByField.set(f.id, out);
            }
          }
          return fields.map((f) =>
            viewTupleField(
              send,
              f,
              predicate,
              subId(f.id),
              current[f.id],
              refCandidatesByField.get(f.id) ?? [],
              commit,
              entityId,
              host,
            ),
          );
        },
      }),
    ],
  );
}

/** One sub-control of a tuple, by field kind. */
function viewTupleField<Model, Msg>(
  send: Send<Msg>,
  field: TupleFieldDeclaration,
  predicate: string,
  id: string,
  value: unknown,
  refCandidates: RefOption[],
  commit: () => void,
  entityId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const labelText = resolveLocalizedString(field.label) || field.id;
  const valStr = value === undefined || value === null ? '' : String(value);
  const onCommit = (): void => {
    send(host.markDirty(entityId, predicate));
    commit();
  };

  let control: Mountable;
  if (field.value_kind === 'ref') {
    control = select(
      {
        id,
        class: 'dl-select dl-select--full',
        value: valStr,
        'data-testid': `tuple-${predicate}-${field.id}`,
        onChange: onCommit,
      },
      ((): Mountable[] => {
        const opts: Mountable[] = [option({ value: '' }, [labelText])];
        for (const c of refCandidates) opts.push(option({ value: c.id }, [c.name]));
        return opts;
      })(),
    );
  } else if (field.value_kind === 'enum') {
    control = select(
      {
        id,
        class: 'dl-select dl-select--full',
        value: valStr,
        'data-testid': `tuple-${predicate}-${field.id}`,
        onChange: onCommit,
      },
      ((): Mountable[] => {
        const opts: Mountable[] = [option({ value: '' }, [labelText])];
        // Enum options STORE the opaque `enm_` id, DISPLAY the localized
        // name (ADR-0004 §1 — identity is the id, never the display name).
        for (const v of field.enum_values ?? [])
          opts.push(option({ value: v.id }, [resolveLocalizedString(v.label)]));
        return opts;
      })(),
    );
  } else {
    const inputType =
      field.value_kind === 'number'
        ? 'number'
        : field.value_kind === 'real_date'
          ? 'date'
          : field.value_kind === 'real_time' || field.value_kind === 'game_time'
            ? 'time'
            : 'text';
    control = input({
      type: inputType,
      id,
      value: valStr,
      placeholder: labelText,
      'data-testid': `tuple-${predicate}-${field.id}`,
      onInput: () => send(host.markDirty(entityId, predicate)),
      onChange: onCommit,
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === 'Enter') onCommit();
      },
    });
  }

  return div({ class: 'fact-tuple-field' }, [
    label({ for: id, class: 'fact-sub-label' }, [labelText + (field.optional ? '' : ' *')]),
    control,
  ]);
}

function viewBooleanInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  // Optional per-state labels (e.g. "Walled" / "Open" instead of the bare
  // checkbox). When declared, render the resolved label of the CURRENT
  // state beside the box so the meaning of "checked" is explicit and the
  // colour is never the only signal (structural a11y invariant).
  const labels = decl.boolean_labels;
  const checkbox = input({
    type: 'checkbox',
    name: predicate,
    id: fieldId,
    checked: state.map((s) => Boolean(currentValue(factAtPredicate(s, entityId, predicate)))),
    'data-testid': `fact-${predicate}`,
    'data-value-kind': 'boolean',
    'aria-label': predicateLabel(predicate),
    'aria-describedby': `${fieldId}-error`,
    onFocus: () => send(host.focusFact(entityId, predicate)),
    onBlur: () => send(host.blurFact(entityId, predicate)),
    onChange: (event: Event) => {
      // #156 — checkbox toggles commit immediately, so dirty + saved
      // both fire within the same tick. The dirty cue is brief; the
      // saved badge surfaces for ~1500ms (the standard timeout).
      const next = (event.target as HTMLInputElement).checked;
      send(host.markDirty(entityId, predicate));
      send(host.editFact(entityId, predicate, next));
    },
  });
  if (!labels) return checkbox;
  return label({ class: 'fact-boolean', for: fieldId }, [
    checkbox,
    span({ class: 'fact-boolean-state', 'data-testid': `fact-${predicate}-state` }, [
      text(
        state.map((s) =>
          currentValue(factAtPredicate(s, entityId, predicate))
            ? resolveLocalizedString(labels.true_label)
            : resolveLocalizedString(labels.false_label),
        ),
      ),
    ]),
  ]);
}

function viewEnumInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  const options = decl.enum_values ?? [];

  return select(
    {
      name: predicate,
      id: fieldId,
      'data-testid': `fact-${predicate}`,
      'data-value-kind': 'enum',
      'aria-label': predicateLabel(predicate),
      'aria-describedby': `${fieldId}-error`,
      value: state.map((s) => {
        const v = currentValue(factAtPredicate(s, entityId, predicate));
        return v === undefined || v === null ? '' : String(v);
      }),
      onFocus: () => send(host.focusFact(entityId, predicate)),
      onBlur: () => send(host.blurFact(entityId, predicate)),
      onChange: (event: Event) => {
        // #156 — select commits immediately, mirroring the checkbox path.
        const next = (event.target as HTMLSelectElement).value;
        send(host.markDirty(entityId, predicate));
        if (next === '') {
          send(host.softDeleteFact(entityId, predicate));
          return;
        }
        const result = validateValueWithDeclaration(decl, next);
        if (!result.ok) {
          send(host.invalidFact(entityId, predicate, result.error));
          return;
        }
        send(host.editFact(entityId, predicate, result.value));
      },
    },
    [
      option({ value: '' }, [text(predicatePlaceholder(predicate))]),
      each(
        // Each option STORES the opaque `enm_` id and DISPLAYS its
        // localized name (ADR-0004 §1). Keyed by the stable id.
        state.map(() =>
          options.map((v) => ({ id: v.id, label: resolveLocalizedString(v.label) })),
        ),
        {
          key: (row) => row.id,
          render: (item) => [
            option({ value: item.at('id') }, [text(item.at('label'))]),
          ],
        },
      ),
    ],
  );
}

/**
 * Color picker for the `color` value kind (ADR-0004 §6, #168/#182/#219).
 * The shared `@dl/ui` `colorPicker` primitive: a curated swatch row (the
 * §10.2.1 campaign-accent palette) over a native `<input type="color">` +
 * a hex text field. The active swatch's check glyph and the hex string are
 * the **non-color a11y cues** (colour is never the only signal; structural
 * invariant). Every control commits the same `#rrggbb`(`aa`) value,
 * validated through `isHexColor` + the declaration before the substrate
 * write, so a malformed manual entry surfaces as an invalid-fact, not a
 * silent bad store (a curated swatch is always valid).
 */
function viewColorInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;

  const commit = (raw: string): void => {
    const value = raw.trim();
    send(host.markDirty(entityId, predicate));
    if (value === '') {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    if (!isHexColor(value)) {
      send(host.invalidFact(entityId, predicate, t('fact.color.invalid')));
      return;
    }
    const result = validateValueWithDeclaration(decl, value);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  const currentHex = state.map((s) => {
    const v = currentValue(factAtPredicate(s, entityId, predicate));
    return v === undefined || v === null ? '' : String(v);
  });

  return colorPicker({
    value: currentHex,
    swatches: campaignAccentSwatches(),
    onChange: commit,
    // The predicate's own label is the group label; the rest of the copy
    // (swatch names, "Custom color", hex) is shared.
    messages: { ...colorPickerMessages(), group: predicateLabel(predicate) },
    id: fieldId,
    hexTestid: `fact-${predicate}`,
    onFocus: () => send(host.focusFact(entityId, predicate)),
    onBlur: () => send(host.blurFact(entityId, predicate)),
    hexAttrs: { 'aria-describedby': `${fieldId}-error`, 'data-value-kind': 'color' },
  });
}

// ─── geometry / interior_frame input renderers (#202 / #203) ──────────────
//
// No map canvas in this slice (05-geography.md): geometries + frames are
// authored as structured fields. Coordinates are in the parent frame's units
// (no global CRS). Every control validates the assembled value through the
// declaration before the substrate write — a malformed payload surfaces as an
// invalid-fact (the standard `host.invalidFact` channel), never a silent bad
// store. The keyed `each` over the stored value digest gives a build-once atom
// that re-seeds the uncontrolled sub-inputs when the fact changes externally
// (AI draft landing, navigation, validity supersession).

/** Read the current value of a geometry/frame fact as a plain record, or `null`. */
function readStructuredValue<Model extends FactRowStateShape>(
  s: Model,
  entityId: string,
  predicate: string,
): Record<string, unknown> | null {
  const v = currentValue(factAtPredicate(s, entityId, predicate));
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/** A digest of the stored value so the keyed `each` re-seeds on external change. */
function structuredDigest<Model extends FactRowStateShape>(
  s: Model,
  entityId: string,
  predicate: string,
): string {
  const v = readStructuredValue(s, entityId, predicate);
  return v === null ? '' : JSON.stringify(v);
}

/**
 * `geometry_point` editor (05-geography.md §12 — a precise position): two
 * numeric coordinate inputs (X / Y, in the parent frame's units) plus an
 * optional `alt_description` for accessibility. Commits on any input change.
 */
function viewGeometryPointInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  const xId = `${fieldId}-x`;
  const yId = `${fieldId}-y`;
  const altId = `${fieldId}-alt`;

  const commit = (): void => {
    send(host.markDirty(entityId, predicate));
    const xEl = document.getElementById(xId) as HTMLInputElement | null;
    const yEl = document.getElementById(yId) as HTMLInputElement | null;
    const altEl = document.getElementById(altId) as HTMLInputElement | null;
    const xRaw = xEl?.value.trim() ?? '';
    const yRaw = yEl?.value.trim() ?? '';
    const alt = altEl?.value.trim() ?? '';
    if (xRaw === '' && yRaw === '' && alt === '') {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    const x = Number(xRaw);
    const y = Number(yRaw);
    if (xRaw === '' || yRaw === '' || !Number.isFinite(x) || !Number.isFinite(y)) {
      send(host.invalidFact(entityId, predicate, t('fact.geometry.invalid_point')));
      return;
    }
    const value: Record<string, unknown> = { x, y };
    if (alt !== '') value['alt_description'] = alt;
    const result = validateValueWithDeclaration(decl, value);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  return div(
    {
      class: 'fact-geometry fact-geometry--point',
      id: fieldId,
      role: 'group',
      'data-value-kind': 'geometry_point',
      'aria-label': predicateLabel(predicate),
    },
    [
      each(state.map((s) => [structuredDigest(s, entityId, predicate)] as const), {
        key: (k) => k,
        render: () => {
          const cur = readStructuredValue(state.peek(), entityId, predicate) ?? {};
          const numStr = (k: string): string =>
            typeof cur[k] === 'number' ? String(cur[k] as number) : '';
          const altStr = typeof cur['alt_description'] === 'string' ? (cur['alt_description'] as string) : '';
          return [
            label({ class: 'fact-geometry-field' }, [
              span({ class: 'fact-geometry-lbl' }, [text(t('fact.geometry.x'))]),
              input({
                type: 'number',
                id: xId,
                class: 'fact-geometry-coord',
                value: numStr('x'),
                'data-testid': `fact-${predicate}-x`,
                onChange: commit,
              }),
            ]),
            label({ class: 'fact-geometry-field' }, [
              span({ class: 'fact-geometry-lbl' }, [text(t('fact.geometry.y'))]),
              input({
                type: 'number',
                id: yId,
                class: 'fact-geometry-coord',
                value: numStr('y'),
                'data-testid': `fact-${predicate}-y`,
                onChange: commit,
              }),
            ]),
            viewGeometryAltField(altId, predicate, altStr, commit),
          ];
        },
      }),
    ],
  );
}

/**
 * `geometry_line` / `geometry_polygon` editor: a textarea of `x, y` coordinate
 * lines (one per row) plus an optional `alt_description`. A line needs ≥2
 * points, a polygon ≥3 (enforced by the declaration's schema on commit — a
 * malformed list surfaces as an invalid-fact). Interior `holes` are preserved
 * on round-trip when present on the existing value but are not authored in this
 * minimal v1 control (no map canvas — §12).
 */
function viewGeometryPathInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  kind: ValueKind,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  const pointsId = `${fieldId}-points`;
  const altId = `${fieldId}-alt`;
  const isPolygon = kind === 'geometry_polygon';

  const commit = (): void => {
    send(host.markDirty(entityId, predicate));
    const pointsEl = document.getElementById(pointsId) as HTMLTextAreaElement | null;
    const altEl = document.getElementById(altId) as HTMLInputElement | null;
    const raw = pointsEl?.value ?? '';
    const alt = altEl?.value.trim() ?? '';
    const lines = raw
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l !== '');
    if (lines.length === 0 && alt === '') {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    const points: { x: number; y: number }[] = [];
    let parseOk = true;
    for (const line of lines) {
      const parts = line.split(',').map((p) => p.trim());
      const x = Number(parts[0]);
      const y = Number(parts[1]);
      if (parts.length !== 2 || parts[0] === '' || parts[1] === '' || !Number.isFinite(x) || !Number.isFinite(y)) {
        parseOk = false;
        break;
      }
      points.push({ x, y });
    }
    const invalidMsg = isPolygon
      ? t('fact.geometry.invalid_polygon')
      : t('fact.geometry.invalid_line');
    if (!parseOk || points.length < (isPolygon ? 3 : 2)) {
      send(host.invalidFact(entityId, predicate, invalidMsg));
      return;
    }
    // Preserve `holes` from the existing value (round-trip — they aren't
    // authored in this minimal control but must survive an alt/points edit).
    const existing = readStructuredValue(state.peek(), entityId, predicate);
    const value: Record<string, unknown> = { points };
    if (isPolygon && existing && Array.isArray(existing['holes'])) {
      value['holes'] = existing['holes'];
    }
    if (alt !== '') value['alt_description'] = alt;
    const result = validateValueWithDeclaration(decl, value);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  return div(
    {
      class: 'fact-geometry fact-geometry--path',
      id: fieldId,
      role: 'group',
      'data-value-kind': kind,
      'aria-label': predicateLabel(predicate),
    },
    [
      each(state.map((s) => [structuredDigest(s, entityId, predicate)] as const), {
        key: (k) => k,
        render: () => {
          const cur = readStructuredValue(state.peek(), entityId, predicate) ?? {};
          const pts = Array.isArray(cur['points']) ? (cur['points'] as unknown[]) : [];
          const pointsText = pts
            .map((p) =>
              p && typeof p === 'object'
                ? `${(p as { x?: unknown }).x ?? ''}, ${(p as { y?: unknown }).y ?? ''}`
                : '',
            )
            .join('\n');
          const altStr = typeof cur['alt_description'] === 'string' ? (cur['alt_description'] as string) : '';
          return [
            label({ class: 'fact-geometry-field fact-geometry-field-block' }, [
              span({ class: 'fact-geometry-lbl' }, [text(t('fact.geometry.points_label'))]),
              textarea({
                id: pointsId,
                class: 'fact-geometry-points',
                rows: '4',
                value: pointsText,
                placeholder: t('fact.geometry.points_placeholder'),
                'data-testid': `fact-${predicate}-points`,
                onChange: commit,
              }),
            ]),
            viewGeometryAltField(altId, predicate, altStr, commit),
          ];
        },
      }),
    ],
  );
}

/** Shared optional `alt_description` text field for geometry editors (§12). */
function viewGeometryAltField(
  altId: string,
  predicate: string,
  value: string,
  commit: () => void,
): Mountable {
  return label({ class: 'fact-geometry-field fact-geometry-field-block' }, [
    span({ class: 'fact-geometry-lbl' }, [text(t('fact.geometry.alt_label'))]),
    input({
      type: 'text',
      id: altId,
      class: 'fact-geometry-alt',
      value,
      placeholder: t('fact.geometry.alt_placeholder'),
      'data-testid': `fact-${predicate}-alt`,
      onChange: commit,
    }),
  ]);
}

/**
 * `interior_frame` editor (05-geography.md §3 — a Location's renderable
 * coordinate space): structured fields for bounds (W / H, positive), units
 * (free string), origin (corner / center), an optional grid overlay (kind +
 * cell_size), and an optional background asset id. Validity-awareness is the
 * existing fact-validity mechanism — replacing bounds + saving against a new
 * `valid_from` supersedes the prior frame; nothing here encodes time.
 */
function viewInteriorFrameInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  const wId = `${fieldId}-w`;
  const hId = `${fieldId}-h`;
  const unitsId = `${fieldId}-units`;
  const originId = `${fieldId}-origin`;
  const gridKindId = `${fieldId}-grid-kind`;
  const cellId = `${fieldId}-cell`;
  const bgId = `${fieldId}-bg`;

  const commit = (): void => {
    send(host.markDirty(entityId, predicate));
    const get = (id: string): string =>
      (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value.trim() ??
      '';
    const wRaw = get(wId);
    const hRaw = get(hId);
    const units = get(unitsId);
    const origin = get(originId);
    const gridKind = get(gridKindId);
    const cellRaw = get(cellId);
    const bg = get(bgId);

    if (wRaw === '' && hRaw === '' && units === '' && bg === '' && gridKind === '') {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    const w = Number(wRaw);
    const h = Number(hRaw);
    if (wRaw === '' || hRaw === '' || units === '' || !Number.isFinite(w) || !Number.isFinite(h)) {
      send(host.invalidFact(entityId, predicate, t('fact.frame.invalid')));
      return;
    }
    const value: Record<string, unknown> = {
      bounds: { w, h },
      units,
      origin: origin || 'corner',
    };
    if (gridKind !== '') {
      const cell = Number(cellRaw);
      if (cellRaw === '' || !Number.isFinite(cell)) {
        send(host.invalidFact(entityId, predicate, t('fact.frame.invalid')));
        return;
      }
      value['grid'] = { kind: gridKind, cell_size: cell };
    }
    if (bg !== '') value['background'] = bg;
    const result = validateValueWithDeclaration(decl, value);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  return div(
    {
      class: 'fact-frame',
      id: fieldId,
      role: 'group',
      'data-value-kind': 'interior_frame',
      'aria-label': predicateLabel(predicate),
    },
    [
      each(state.map((s) => [structuredDigest(s, entityId, predicate)] as const), {
        key: (k) => k,
        render: () => {
          const cur = readStructuredValue(state.peek(), entityId, predicate) ?? {};
          const bounds =
            cur['bounds'] && typeof cur['bounds'] === 'object'
              ? (cur['bounds'] as Record<string, unknown>)
              : {};
          const grid =
            cur['grid'] && typeof cur['grid'] === 'object'
              ? (cur['grid'] as Record<string, unknown>)
              : null;
          const numStr = (v: unknown): string => (typeof v === 'number' ? String(v) : '');
          const strOf = (v: unknown): string => (typeof v === 'string' ? v : '');
          return [
            label({ class: 'fact-frame-field' }, [
              span({ class: 'fact-frame-lbl' }, [text(t('fact.frame.width'))]),
              input({
                type: 'number',
                id: wId,
                class: 'fact-frame-num',
                value: numStr(bounds['w']),
                'data-testid': `fact-${predicate}-w`,
                onChange: commit,
              }),
            ]),
            label({ class: 'fact-frame-field' }, [
              span({ class: 'fact-frame-lbl' }, [text(t('fact.frame.height'))]),
              input({
                type: 'number',
                id: hId,
                class: 'fact-frame-num',
                value: numStr(bounds['h']),
                'data-testid': `fact-${predicate}-h`,
                onChange: commit,
              }),
            ]),
            label({ class: 'fact-frame-field' }, [
              span({ class: 'fact-frame-lbl' }, [text(t('fact.frame.units'))]),
              input({
                type: 'text',
                id: unitsId,
                class: 'fact-frame-units',
                value: strOf(cur['units']),
                placeholder: t('fact.frame.units_placeholder'),
                'data-testid': `fact-${predicate}-units`,
                onChange: commit,
              }),
            ]),
            label({ class: 'fact-frame-field' }, [
              span({ class: 'fact-frame-lbl' }, [text(t('fact.frame.origin'))]),
              select(
                {
                  id: originId,
                  class: 'dl-select',
                  value: strOf(cur['origin']) || 'corner',
                  'data-testid': `fact-${predicate}-origin`,
                  onChange: commit,
                },
                [
                  option({ value: 'corner' }, [text(t('fact.frame.origin_corner'))]),
                  option({ value: 'center' }, [text(t('fact.frame.origin_center'))]),
                ],
              ),
            ]),
            label({ class: 'fact-frame-field' }, [
              span({ class: 'fact-frame-lbl' }, [text(t('fact.frame.grid'))]),
              select(
                {
                  id: gridKindId,
                  class: 'dl-select',
                  value: grid ? strOf(grid['kind']) : '',
                  'data-testid': `fact-${predicate}-grid-kind`,
                  onChange: commit,
                },
                [
                  option({ value: '' }, [text(t('fact.frame.grid_none'))]),
                  option({ value: 'square' }, [text(t('fact.frame.grid_square'))]),
                  option({ value: 'hex_pointy' }, [text(t('fact.frame.grid_hex_pointy'))]),
                  option({ value: 'hex_flat' }, [text(t('fact.frame.grid_hex_flat'))]),
                ],
              ),
            ]),
            label({ class: 'fact-frame-field' }, [
              span({ class: 'fact-frame-lbl' }, [text(t('fact.frame.cell_size'))]),
              input({
                type: 'number',
                id: cellId,
                class: 'fact-frame-num',
                value: grid ? numStr(grid['cell_size']) : '',
                'data-testid': `fact-${predicate}-cell`,
                onChange: commit,
              }),
            ]),
            label({ class: 'fact-frame-field' }, [
              span({ class: 'fact-frame-lbl' }, [text(t('fact.frame.background'))]),
              input({
                type: 'text',
                id: bgId,
                class: 'fact-frame-bg',
                value: strOf(cur['background']),
                placeholder: t('fact.frame.background_placeholder'),
                'data-testid': `fact-${predicate}-bg`,
                onChange: commit,
              }),
            ]),
          ];
        },
      }),
    ],
  );
}

/**
 * Candidate parents for a containment value: existing entities filtered to the
 * predicate's allowed roots (Location), self excluded, soft-deleted excluded,
 * plus the currently-stored parent (so the select shows it even if it became
 * an off-root / missing entity). Sorted by name. Mirrors `refOptions` but reads
 * the parent out of the structured containment value rather than the bare fact.
 */
function containmentParentOptions<S extends FactRowStateShape>(
  s: S,
  entityId: string,
  predicate: string,
  roots: readonly EntityRoot[],
): RefOption[] {
  const allow = new Set<string>(roots);
  const cur = readStructuredValue(s, entityId, predicate);
  const currentId =
    cur && typeof cur['parent'] === 'string' && cur['parent'].length > 0
      ? (cur['parent'] as string)
      : null;
  const out: RefOption[] = [];
  for (const [id, entity] of Object.entries(s.entities)) {
    if (id === entityId) continue;
    if (entity.soft_deleted) continue;
    if (allow.size > 0 && !allow.has(entity.root) && id !== currentId) continue;
    out.push({ id, name: refEntityName(entity) });
  }
  if (currentId && !out.some((o) => o.id === currentId)) {
    const e = s.entities[currentId];
    out.push({ id: currentId, name: e ? refEntityName(e) : currentId });
  }
  out.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

/**
 * `containment` editor (05-geography.md §2, #206): authors a structured
 * `{ parent, context, position? }` value for the `contained_in` predicate.
 *
 *   - **Parent** — a select of Location candidates (the chosen option's value
 *     IS the target entity id). A containment with no parent is invalid.
 *   - **Context** — a FREE text input (open vocabulary; §2.1). Blank ⇒ the
 *     `geographic` default. The placeholder shows the default.
 *   - **Position** — an optional geometry expressed in the parent's interior
 *     frame: pick a kind (none / point / line / polygon); a point shows X/Y,
 *     a line/polygon shows a coordinate textarea. Holes are round-tripped but
 *     not authored in this minimal v1 control (no map canvas — §12).
 *
 * Multiple contexts coexist as separate fact instances; this control commits
 * the one value bound to its row. The keyed `each` over the value digest
 * reseeds the uncontrolled sub-inputs when the fact changes externally
 * (AI draft landing, navigation, validity supersession). Every commit
 * validates through `validateValueWithDeclaration` — a malformed payload
 * surfaces as an invalid fact, never a silent bad store.
 */
function viewContainmentInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  const roots = allowedRefRoots(decl);
  const parentId = `${fieldId}-parent`;
  const contextId = `${fieldId}-context`;
  const posKindId = `${fieldId}-pos-kind`;
  const posXId = `${fieldId}-pos-x`;
  const posYId = `${fieldId}-pos-y`;
  const posPointsId = `${fieldId}-pos-points`;

  const commit = (): void => {
    send(host.markDirty(entityId, predicate));
    const get = (id: string): string =>
      (
        document.getElementById(id) as
          | HTMLInputElement
          | HTMLSelectElement
          | HTMLTextAreaElement
          | null
      )?.value.trim() ?? '';
    const parent = get(parentId);
    const context = get(contextId);
    const posKind = get(posKindId);

    if (parent === '') {
      // No parent + nothing else ⇒ clear the fact; a context/position without a
      // parent is incomplete, so we flag it rather than store half a value.
      if (context === '' && posKind === '') {
        send(host.softDeleteFact(entityId, predicate));
        return;
      }
      send(host.invalidFact(entityId, predicate, t('fact.containment.invalid_parent')));
      return;
    }

    const value: Record<string, unknown> = { parent };
    // Blank context ⇒ omit it; the schema applies the `geographic` default.
    if (context !== '') value['context'] = context;

    if (posKind === 'point') {
      const x = Number(get(posXId));
      const y = Number(get(posYId));
      if (get(posXId) === '' || get(posYId) === '' || !Number.isFinite(x) || !Number.isFinite(y)) {
        send(host.invalidFact(entityId, predicate, t('fact.containment.invalid_position')));
        return;
      }
      value['position'] = { kind: 'point', value: { x, y } };
    } else if (posKind === 'line' || posKind === 'polygon') {
      const points = parseCoordLines(get(posPointsId));
      const min = posKind === 'polygon' ? 3 : 2;
      if (points === null || points.length < min) {
        send(host.invalidFact(entityId, predicate, t('fact.containment.invalid_position')));
        return;
      }
      const existing = readStructuredValue(state.peek(), entityId, predicate);
      const existingPos =
        existing && existing['position'] && typeof existing['position'] === 'object'
          ? (existing['position'] as Record<string, unknown>)
          : null;
      const pos: Record<string, unknown> = { kind: posKind, value: points };
      // Round-trip polygon holes (not authored here).
      if (posKind === 'polygon' && existingPos && Array.isArray(existingPos['holes'])) {
        pos['holes'] = existingPos['holes'];
      }
      value['position'] = pos;
    }

    const result = validateValueWithDeclaration(decl, value);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  return div(
    {
      class: 'fact-containment',
      id: fieldId,
      role: 'group',
      'data-value-kind': 'containment',
      'aria-label': predicateLabel(predicate),
    },
    [
      each(
        state.map((s) => {
          const digest = structuredDigest(s, entityId, predicate);
          // Re-render when the entity set changes too (parent candidate list).
          return [`${digest}:${Object.keys(s.entities).length}`] as const;
        }),
        {
          key: (k) => k,
          render: (): Renderable => {
            const snap = state.peek();
            const cur = readStructuredValue(snap, entityId, predicate) ?? {};
            const curParent = typeof cur['parent'] === 'string' ? (cur['parent'] as string) : '';
            const curContext = containmentContext(cur) === DEFAULT_CONTAINMENT_CONTEXT
              ? typeof cur['context'] === 'string'
                ? (cur['context'] as string)
                : ''
              : containmentContext(cur);
            const pos =
              cur['position'] && typeof cur['position'] === 'object'
                ? (cur['position'] as Record<string, unknown>)
                : null;
            const posKind = pos && typeof pos['kind'] === 'string' ? (pos['kind'] as string) : '';
            const ptVal =
              posKind === 'point' && pos && pos['value'] && typeof pos['value'] === 'object'
                ? (pos['value'] as Record<string, unknown>)
                : null;
            const numStr = (v: unknown): string => (typeof v === 'number' ? String(v) : '');
            const pathPoints =
              (posKind === 'line' || posKind === 'polygon') && Array.isArray(pos?.['value'])
                ? (pos!['value'] as unknown[])
                    .map((p) =>
                      p && typeof p === 'object'
                        ? `${(p as { x?: unknown }).x ?? ''}, ${(p as { y?: unknown }).y ?? ''}`
                        : '',
                    )
                    .join('\n')
                : '';
            const parentOptions = containmentParentOptions(snap, entityId, predicate, roots);

            return [
              label({ class: 'fact-containment-field' }, [
                span({ class: 'fact-containment-lbl' }, [text(t('fact.containment.parent'))]),
                select(
                  {
                    id: parentId,
                    class: 'dl-select dl-select--full',
                    value: curParent,
                    'data-testid': `fact-${predicate}-parent`,
                    onChange: commit,
                  },
                  [
                    option({ value: '' }, [text(t('fact.containment.parent_placeholder'))]),
                    ...parentOptions.map((o) => option({ value: o.id }, [text(o.name)])),
                  ],
                ),
              ]),
              label({ class: 'fact-containment-field' }, [
                span({ class: 'fact-containment-lbl' }, [text(t('fact.containment.context'))]),
                input({
                  type: 'text',
                  id: contextId,
                  class: 'fact-containment-context',
                  value: curContext,
                  placeholder: t('fact.containment.context_placeholder'),
                  'data-testid': `fact-${predicate}-context`,
                  onChange: commit,
                }),
              ]),
              label({ class: 'fact-containment-field' }, [
                span({ class: 'fact-containment-lbl' }, [text(t('fact.containment.position'))]),
                select(
                  {
                    id: posKindId,
                    class: 'dl-select',
                    value: posKind,
                    'data-testid': `fact-${predicate}-pos-kind`,
                    onChange: commit,
                  },
                  [
                    option({ value: '' }, [text(t('fact.containment.position_none'))]),
                    option({ value: 'point' }, [text(t('fact.containment.position_point'))]),
                    option({ value: 'line' }, [text(t('fact.containment.position_line'))]),
                    option({ value: 'polygon' }, [text(t('fact.containment.position_polygon'))]),
                  ],
                ),
              ]),
              ...(posKind === 'point'
                ? [
                    label({ class: 'fact-containment-field' }, [
                      span({ class: 'fact-containment-lbl' }, [text(t('fact.geometry.x'))]),
                      input({
                        type: 'number',
                        id: posXId,
                        class: 'fact-geometry-coord',
                        value: numStr(ptVal?.['x']),
                        'data-testid': `fact-${predicate}-pos-x`,
                        onChange: commit,
                      }),
                      span({ class: 'fact-containment-lbl' }, [text(t('fact.geometry.y'))]),
                      input({
                        type: 'number',
                        id: posYId,
                        class: 'fact-geometry-coord',
                        value: numStr(ptVal?.['y']),
                        'data-testid': `fact-${predicate}-pos-y`,
                        onChange: commit,
                      }),
                    ]),
                  ]
                : []),
              ...(posKind === 'line' || posKind === 'polygon'
                ? [
                    label({ class: 'fact-containment-field fact-containment-field-block' }, [
                      span({ class: 'fact-containment-lbl' }, [
                        text(t('fact.geometry.points_label')),
                      ]),
                      textarea({
                        id: posPointsId,
                        class: 'fact-geometry-points',
                        rows: '4',
                        value: pathPoints,
                        placeholder: t('fact.geometry.points_placeholder'),
                        'data-testid': `fact-${predicate}-pos-points`,
                        onChange: commit,
                      }),
                    ]),
                  ]
                : []),
            ];
          },
        },
      ),
    ],
  );
}

/**
 * Parse a textarea of `x, y` coordinate lines into points, or `null` if any
 * line is malformed. Shared by the containment position editor; mirrors the
 * geometry path editor's parse. Empty input yields `[]`.
 */
function parseCoordLines(raw: string): { x: number; y: number }[] | null {
  const lines = raw
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '');
  const points: { x: number; y: number }[] = [];
  for (const line of lines) {
    const parts = line.split(',').map((p) => p.trim());
    const x = Number(parts[0]);
    const y = Number(parts[1]);
    if (parts.length !== 2 || parts[0] === '' || parts[1] === '' || !Number.isFinite(x) || !Number.isFinite(y)) {
      return null;
    }
    points.push({ x, y });
  }
  return points;
}

/**
 * Relationship (ref) picker — establishes an edge between entities via
 * a fact whose value is the target entity's id. Renders a select of
 * existing entities filtered to the predicate's allowed roots
 * (`allowedRefRoots`); the chosen option's value IS the target id, so
 * committing writes a real entity reference (not free text). Selecting
 * the blank option soft-deletes the relationship.
 *
 * Mirrors viewEnumInput's reactive-select shape (reactive `value` +
 * h.each options) so adding an entity refreshes the candidate list.
 * The `dl-select` class gives it the design-system input chrome.
 *
 * Stub-on-reference (pick a name that doesn't exist yet → auto-create
 * a stub) is a follow-up; v1 references existing entities.
 */
function viewRefInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  const roots = allowedRefRoots(decl);
  return select(
    {
      name: predicate,
      id: fieldId,
      class: 'dl-select dl-select--full',
      'data-testid': `fact-${predicate}`,
      'data-value-kind': 'ref',
      'aria-label': predicateLabel(predicate),
      'aria-describedby': `${fieldId}-error`,
      value: state.map((s) => {
        const v = currentValue(factAtPredicate(s, entityId, predicate));
        return v === undefined || v === null ? '' : String(v);
      }),
      onFocus: () => send(host.focusFact(entityId, predicate)),
      onBlur: () => send(host.blurFact(entityId, predicate)),
      onChange: (event: Event) => {
        const next = (event.target as HTMLSelectElement).value;
        send(host.markDirty(entityId, predicate));
        if (next === '') {
          send(host.softDeleteFact(entityId, predicate));
          return;
        }
        const result = validateValueWithDeclaration(decl, next);
        if (!result.ok) {
          send(host.invalidFact(entityId, predicate, result.error));
          return;
        }
        send(host.editFact(entityId, predicate, result.value));
      },
    },
    [
      option({ value: '' }, [text(predicatePlaceholder(predicate))]),
      each(
        state.map((s) => refOptions(s, entityId, predicate, roots)),
        {
          key: (row) => row.id,
          render: (item) => [
            option({ value: item.at('id') }, [text(item.at('name'))]),
          ],
        },
      ),
    ],
  );
}

function viewAssetInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  kind: ValueKind,
  host: FactRowHost<Model, Msg>,
): Mountable {
  // `image` shows the inline preview; `file` / `asset` show the id input
  // (a generic upload has no thumbnail). The accept hint comes from the
  // declaration's media block (image defaults to PNG/WEBP/JPEG).
  const isImage = kind === 'image' || kind === 'asset';
  const accept =
    decl.media?.accept && decl.media.accept.length > 0
      ? decl.media.accept.join(',')
      : kind === 'image'
        ? DEFAULT_IMAGE_ACCEPT.join(',')
        : undefined;
  // The asset uploader path is wired in #60 against the asset
  // metadata endpoint. The substrate-level value is the opaque
  // `asset_id` string; the renderer surfaces a stub uploader that
  // surfaces an error until the worker endpoint is wired into the
  // client. The substrate writes go through the same `edit-fact`
  // pathway as every other value-kind.
  //
  // The minimal Phase 1 shape: a text input that accepts an asset
  // id and a hint that drop / paste / pick come online in #135. The
  // full <AssetField> primitive ships in `@dl/ui`; we mount it
  // through a scope so the upload state stays local-per-row.
  const predicate = decl.id;

  const commit = (raw: string): void => {
    if (raw === '') {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    send(host.editFact(entityId, predicate, raw));
  };

  const idInput = textInput({
    value: state.map((s) => {
      const v = currentValue(factAtPredicate(s, entityId, predicate));
      return v === undefined || v === null ? '' : String(v);
    }),
    placeholder: predicatePlaceholder(predicate),
    testid: `fact-${predicate}`,
    ariaLabel: predicateLabel(predicate),
    onInput: () => {
      send(host.markDirty(entityId, predicate));
    },
    attrs: {
      name: predicate,
      id: fieldId,
      'data-value-kind': kind,
      ...(accept ? { 'data-accept': accept } : {}),
      'aria-describedby': `${fieldId}-error`,
      onFocus: () => send(host.focusFact(entityId, predicate)),
      onBlur: () => {
        // No commit on blur — dirty state stays visible.
        send(host.blurFact(entityId, predicate));
      },
      onKeyDown: (event2: KeyboardEvent) => {
        if (event2.key === 'Enter') {
          commit((event2.target as HTMLInputElement).value);
        }
      },
    },
  });

  // Real upload affordance (#8/#9): a file picker that uploads to the
  // campaign asset store and commits the returned asset id. The hidden
  // <input type=file> sits under a styled <label> (keyboard-reachable);
  // `accept` constrains the picker to the predicate's formats. On success
  // we mark dirty + commit the asset id (the resolved URL flows in via the
  // standard asset-url effect on the next fold). The id text input remains
  // as a power-user fallback (paste a known asset id).
  const uploadInputId = `${fieldId}-upload`;
  const uploadControl = label(
    { class: 'asset-field-upload', for: uploadInputId },
    [
      span({ class: 'asset-field-upload-label' }, [text(t('entity.facts.upload'))]),
      input({
        type: 'file',
        id: uploadInputId,
        class: 'asset-field-upload-input',
        'data-testid': `fact-${predicate}-upload`,
        ...(accept ? { accept } : {}),
        'aria-label': t('entity.facts.upload'),
        onChange: (event: Event) => {
          const fileList = (event.target as HTMLInputElement).files;
          const file = fileList && fileList.length > 0 ? fileList[0] : null;
          if (!file) return;
          send(host.markDirty(entityId, predicate));
          void host
            .uploadAsset(file)
            .then((res) => {
              send(host.editFact(entityId, predicate, res.id));
            })
            .catch((err: unknown) => {
              send(
                host.invalidFact(
                  entityId,
                  predicate,
                  err instanceof Error ? err.message : t('entity.facts.upload_failed'),
                ),
              );
            });
        },
      }),
    ],
  );

  // A `file` field has no thumbnail — upload control + the id input.
  if (!isImage) {
    return div({ class: 'asset-field asset-field--file' }, [uploadControl, idInput]);
  }

  // Render the resolved image inline (editor view == reader view). The
  // preview is always present; `data-resolved` + CSS hide it until a signed
  // URL lands, so the per-row `<img>` src can stay a reactive binding (a
  // per-row `h.show` reading the value isn't available here).
  const rowKey = factRowKey(entityId, predicate);
  // `data-state` picks the preview content: `generating` (a ✨ image
  // request is in flight — shows a shimmer so the GM sees something is
  // happening, not a blank box), `ready` (a signed URL resolved → the
  // image), or `empty` (no asset + nothing generating → collapsed).
  return div({ class: 'asset-field' }, [
    div(
      {
        class: 'asset-field-preview',
        'data-state': state.map((s) =>
          aiDraftPendingAtKey(s, rowKey)
            ? 'generating'
            : assetUrlAtPredicate(s, entityId, predicate) === ''
              ? 'empty'
              : 'ready',
        ),
      },
      [
        span(
          { class: 'asset-field-generating', 'aria-live': 'polite' },
          [text(t('entity.facts.ai_image_pending'))],
        ),
        img({
          class: 'asset-field-image',
          'data-testid': `fact-${predicate}-image`,
          alt: predicateLabel(predicate),
          src: state.map((s) => assetUrlAtPredicate(s, entityId, predicate)),
        }),
      ],
    ),
    uploadControl,
    idInput,
  ]);
}

// ─── datetime helpers ──────────────────────────────────────────────
//
// `<input type="datetime-local">` exchanges values as
// `YYYY-MM-DDTHH:MM` with NO timezone offset. The substrate's
// `real_datetime` schema requires an explicit offset, so we expand
// the local-input value with the viewer's current offset on write
// and strip the offset on read (preserving the original offset in a
// data attribute so the input value round-trips without drift).

function isoToLocalDatetime(iso: string): string {
  // ISO with offset → local input "YYYY-MM-DDTHH:MM" stripping
  // the seconds + zone. We render the date as the original wall
  // clock (not converted to the viewer's TZ) so the editor displays
  // the originally-authored instant.
  // Match "YYYY-MM-DDTHH:MM" up to seconds / offset.
  const m = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/.exec(iso);
  return m && m[1] ? m[1] : '';
}

function localDatetimeToIso(local: string): string {
  if (!local) return '';
  // Build an offset using the viewer's current locale. The browser
  // accepts "YYYY-MM-DDTHH:MM" with no seconds; we append ":00"
  // and the viewer's TZ offset so the substrate schema passes.
  const offset = formatTzOffset(new Date());
  return `${local}:00${offset}`;
}

function formatTzOffset(d: Date): string {
  const totalMin = -d.getTimezoneOffset();
  const sign = totalMin >= 0 ? '+' : '-';
  const abs = Math.abs(totalMin);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
}

function viewDatetimeInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;

  const commit = (raw: string): void => {
    if (raw === '') {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    const iso = localDatetimeToIso(raw);
    const result = validateValueWithDeclaration(decl, iso);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  return input({
    type: 'datetime-local',
    name: predicate,
    id: fieldId,
    value: state.map((s) => {
      const v = currentValue(factAtPredicate(s, entityId, predicate));
      if (typeof v !== 'string') return '';
      return isoToLocalDatetime(v);
    }),
    'data-testid': `fact-${predicate}`,
    'data-value-kind': 'real_datetime',
    'aria-label': predicateLabel(predicate),
    'aria-describedby': `${fieldId}-error`,
    onFocus: () => send(host.focusFact(entityId, predicate)),
    onBlur: () => send(host.blurFact(entityId, predicate)),
    onChange: (event: Event) => {
      // #156 — datetime-local commits on change. Mark dirty then commit;
      // the savedAt update is automatic via the edit-fact reducer arm.
      const raw = (event.target as HTMLInputElement).value;
      send(host.markDirty(entityId, predicate));
      commit(raw);
    },
  });
}

function viewDatetimeRangeInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;

  const commit = (from: string, to: string): void => {
    if (from === '' && to === '') {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    if (from === '' || to === '') {
      send(
        host.invalidFact(entityId, predicate, t('entity.invalid_datetime_range_partial')),
      );
      return;
    }
    const fromIso = localDatetimeToIso(from);
    const toIso = localDatetimeToIso(to);
    if (Date.parse(toIso) < Date.parse(fromIso)) {
      send(host.invalidFact(entityId, predicate, t('entity.invalid_datetime_range_order')));
      return;
    }
    const value = { from: fromIso, to: toIso };
    const result = validateValueWithDeclaration(decl, value);
    if (!result.ok) {
      send(host.invalidFact(entityId, predicate, result.error));
      return;
    }
    send(host.editFact(entityId, predicate, result.value));
  };

  // Local refs for the input pair — we read the partner value off
  // the DOM at commit time so the two inputs commit as a unit.
  const fromId = `${fieldId}-from`;
  const toId = `${fieldId}-to`;

  return div({ class: 'fact-datetime-range', id: fieldId }, [
    label({ for: fromId, class: 'fact-sub-label' }, [text(t('validity.from'))]),
    input({
      type: 'datetime-local',
      name: `${predicate}_from`,
      id: fromId,
      value: state.map(
        (s) => readDatetimeRangePair(factAtPredicate(s, entityId, predicate)).from,
      ),
      'data-testid': `fact-${predicate}-from`,
      'data-value-kind': 'real_datetime_range',
      'aria-describedby': `${fieldId}-error`,
      onFocus: () => send(host.focusFact(entityId, predicate)),
      onBlur: () => send(host.blurFact(entityId, predicate)),
      onChange: (event: Event) => {
        // #156 — see datetime-local handler.
        const from = (event.target as HTMLInputElement).value;
        const toEl = document.getElementById(toId) as HTMLInputElement | null;
        send(host.markDirty(entityId, predicate));
        commit(from, toEl?.value ?? '');
      },
    }),
    label({ for: toId, class: 'fact-sub-label' }, [text(t('validity.to'))]),
    input({
      type: 'datetime-local',
      name: `${predicate}_to`,
      id: toId,
      value: state.map(
        (s) => readDatetimeRangePair(factAtPredicate(s, entityId, predicate)).to,
      ),
      'data-testid': `fact-${predicate}-to`,
      'data-value-kind': 'real_datetime_range',
      'aria-describedby': `${fieldId}-error`,
      onFocus: () => send(host.focusFact(entityId, predicate)),
      onBlur: () => send(host.blurFact(entityId, predicate)),
      onChange: (event: Event) => {
        const to = (event.target as HTMLInputElement).value;
        const fromEl = document.getElementById(fromId) as HTMLInputElement | null;
        send(host.markDirty(entityId, predicate));
        commit(fromEl?.value ?? '', to);
      },
    }),
  ]);
}

// ─── multi-cardinality wrapper ─────────────────────────────────────

function viewMultiInput<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  fieldId: string,
  kind: ValueKind,
  host: FactRowHost<Model, Msg>,
): Mountable {
  const predicate = decl.id;
  const roots = allowedRefRoots(decl);

  const setIdx = (next: unknown[], idx: number, raw: string): unknown[] => {
    const copy = next.slice();
    copy[idx] = raw;
    return copy;
  };

  const commit = (next: unknown[]): void => {
    // Drop empty entries (an unfilled ref row, a blanked text row) so the
    // stored array stays clean; an empty array soft-deletes the fact.
    const cleaned = next.filter((v) => !(typeof v === 'string' && v.trim() === ''));
    if (cleaned.length === 0) {
      send(host.softDeleteFact(entityId, predicate));
      return;
    }
    send(host.editFact(entityId, predicate, cleaned));
  };

  return div(
    {
      class: 'fact-multi',
      id: fieldId,
      'data-testid': `fact-${predicate}-multi`,
      'data-value-kind': kind,
      role: 'group',
      'aria-label': predicateLabel(predicate),
    },
    [
      each(
        // For ref kind the candidate list depends on the entity set, so
        // re-render when an entity is added/removed (entity count) too —
        // otherwise a freshly-created entity won't appear in the row
        // pickers until the row count happens to change. The single-element
        // `[key]` array makes the keyed `each` remount its body when the key
        // string changes — the 0.5.x replacement for `h.scope`.
        state.map((s) => {
          const len = readMultiArr(factAtPredicate(s, entityId, predicate)).length;
          return [
            kind === 'ref'
              ? `${len}:${Object.keys(s.entities).length}`
              : String(len),
          ] as const;
        }),
        {
          key: (k) => k,
          render: (): Renderable => {
        // Capture the snapshot once at render time. The scope key
        // (array length) drives re-renders when the list size changes;
        // mid-list value mutations refresh through the body re-mount.
        // Event handlers reference the snapshot captured at render time
        // so they never read a signal from outside the render cycle.
        const snapshot = state.peek();
        const values = readMultiArr(factAtPredicate(snapshot, entityId, predicate));
        const refCandidates =
          kind === 'ref' ? refOptions(snapshot, entityId, predicate, roots) : [];
        const inputType =
          kind === 'number'
            ? 'number'
            : kind === 'real_datetime' || kind === 'real_datetime_range'
              ? 'datetime-local'
              : 'text';
        // Always render one trailing EMPTY draft row beyond the committed
        // values. The substrate can't hold an empty entry (commit() filters
        // them), so an "Add" that appends '' is a no-op — instead the ready
        // empty row IS the add affordance: filling it commits a new value
        // and the length-keyed scope re-renders with a fresh trailing row.
        // `setIdx(values, values.length, …)` extends the array on commit.
        const refOptionNodes = (): Mountable[] => {
          const opts: Mountable[] = [option({ value: '' }, [predicatePlaceholder(predicate)])];
          for (const c of refCandidates) opts.push(option({ value: c.id }, [c.name]));
          return opts;
        };
        // COMMITTED rows only — one per stored value (length == values.length),
        // so this list grows by exactly one when a value commits; llui
        // reconciles a position-stable list cleanly. The earlier approach
        // rendered a trailing empty placeholder INSIDE this map
        // (`[...values, '']`), but a trailing <select> there failed to
        // reconcile when committing appended a row, so the ref picker never
        // took the selection. The "add another" control is a SEPARATE entry
        // row below.
        const rows: Mountable[] = values.map((v, idx) =>
          div({ class: 'fact-multi-row', 'data-draft': 'false' }, [
            // ref rows are entity pickers; everything else is the typed
            // input. The select's chosen value IS the target entity id.
            kind === 'ref'
              ? select(
                  {
                    name: `${predicate}[${idx}]`,
                    id: `${fieldId}-${idx}`,
                    class: 'dl-select dl-select--full',
                    value: v === undefined || v === null ? '' : String(v),
                    'data-testid': `fact-${predicate}-item-${idx}`,
                    'aria-label': `${predicateLabel(predicate)} ${idx + 1}`,
                    onFocus: () => send(host.focusFact(entityId, predicate)),
                    onBlur: () => send(host.blurFact(entityId, predicate)),
                    onChange: (event: Event) => {
                      const next = (event.target as HTMLSelectElement).value;
                      send(host.markDirty(entityId, predicate));
                      if (next === '') {
                        const copy = values.slice();
                        copy.splice(idx, 1);
                        commit(copy);
                        return;
                      }
                      commit(setIdx(values, idx, next));
                    },
                  },
                  refOptionNodes(),
                )
              : input({
                  type: inputType,
                  name: `${predicate}[${idx}]`,
                  id: `${fieldId}-${idx}`,
                  value: v === undefined || v === null ? '' : String(v),
                  'data-testid': `fact-${predicate}-item-${idx}`,
                  'aria-label': `${predicateLabel(predicate)} ${idx + 1}`,
                  onFocus: () => send(host.focusFact(entityId, predicate)),
                  onInput: () => send(host.markDirty(entityId, predicate)),
                  onBlur: () => send(host.blurFact(entityId, predicate)),
                  onKeyDown: (event: KeyboardEvent) => {
                    if (event.key === 'Enter') {
                      const raw = (event.target as HTMLInputElement).value;
                      commit(setIdx(values, idx, raw));
                    }
                  },
                }),
            uiButton({
              variant: 'icon',
              type: 'button',
              className: 'fact-multi-remove',
              testid: `fact-${predicate}-remove-${idx}`,
              ariaLabel: t('entity.multi.remove_item'),
              onClick: () => {
                const copy = values.slice();
                copy.splice(idx, 1);
                commit(copy);
              },
              children: ['×'],
            }),
          ]),
        );
        // The always-present "new entry" row. Its control is value-empty;
        // committing APPENDS to `values` (it does not edit a slot), so the
        // committed-rows map above gains a row and this entry resets to empty
        // on the next render. No empty entry is ever persisted (commit()
        // drops blanks). This separate row is what makes "add another" work.
        const entryId = `${fieldId}-new`;
        rows.push(
          div({ class: 'fact-multi-row', 'data-draft': 'true' }, [
            kind === 'ref'
              ? select(
                  {
                    name: `${predicate}[new]`,
                    id: entryId,
                    class: 'dl-select dl-select--full',
                    value: '',
                    'data-testid': `fact-${predicate}-item-new`,
                    'aria-label': t('entity.multi.add_item'),
                    onFocus: () => send(host.focusFact(entityId, predicate)),
                    onChange: (event: Event) => {
                      const next = (event.target as HTMLSelectElement).value;
                      if (next === '') return;
                      send(host.markDirty(entityId, predicate));
                      commit([...values, next]);
                    },
                  },
                  refOptionNodes(),
                )
              : input({
                  type: inputType,
                  name: `${predicate}[new]`,
                  id: entryId,
                  value: '',
                  placeholder: predicatePlaceholder(predicate),
                  'data-testid': `fact-${predicate}-item-new`,
                  'aria-label': t('entity.multi.add_item'),
                  onFocus: () => send(host.focusFact(entityId, predicate)),
                  onInput: () => send(host.markDirty(entityId, predicate)),
                  onKeyDown: (event: KeyboardEvent) => {
                    if (event.key === 'Enter') {
                      const raw = (event.target as HTMLInputElement).value;
                      if (raw.trim() === '') return;
                      commit([...values, raw]);
                    }
                  },
                }),
          ]),
        );
        // "Add" focuses the always-present entry row (a fresh empty
        // input/select is already rendered), so clicking it drops the cursor
        // into a ready slot rather than committing an empty entry.
        rows.push(
          uiButton({
            variant: 'secondary',
            type: 'button',
            className: 'fact-multi-add',
            testid: `fact-${predicate}-add`,
            onClick: () => {
              const el = document.getElementById(entryId);
              if (el instanceof HTMLElement) el.focus();
            },
            children: [text(t('entity.multi.add_item'))],
          }),
        );
        return rows;
          },
        },
      ),
    ],
  );
}

// ────────────────────────────────────────────────────────────────────
// viewFactChips — non-default-only chip render (#121).
//
// Lifted from app.ts verbatim, generalised over the host so the chip
// container is emitted once per row regardless of value_kind.
// ────────────────────────────────────────────────────────────────────

function viewFactChips<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  predicate: string,
  entityId: string,
  host: FactRowHost<Model, Msg>,
): Renderable {
  return [
    div({ class: 'fact-chips', 'data-testid': `fact-chips-${predicate}` }, [
      // Per-attribute reveal chip (Phase 2 / 30 §7.7). Rendered only when the
      // fact has a current value — revealing a never-written fact is
      // meaningless. The chip's glyph reflects effective visibility: ○ hidden
      // (GM-only) vs ◐ partial (revealed to ≥1 Viewer). Clicking opens the
      // Viewer-Matrix popover (owned at the app level). One `show` per state
      // so the chip remounts with the correct `data-state` when visibility
      // flips — matching the status-pill idiom below.
      show(
        state.map(
          (s) =>
            factHasValue(s, entityId, predicate) &&
            (factAtPredicate(s, entityId, predicate)?.reveals.length ?? 0) === 0,
        ),
        () => [
          revealChip({
            state: 'hidden',
            label: t('reveal.chip.hidden'),
            text: t('reveal.chip.hidden_short'),
            onClick: () => send(host.openRevealMenu(entityId, predicate)),
            testid: `fact-reveal-chip-${predicate}`,
          }),
        ],
      ),
      show(
        state.map(
          (s) =>
            factHasValue(s, entityId, predicate) &&
            (factAtPredicate(s, entityId, predicate)?.reveals.length ?? 0) > 0,
        ),
        () => [
          revealChip({
            state: 'partial',
            label: t('reveal.chip.partial'),
            text: t('reveal.chip.partial_short'),
            onClick: () => send(host.openRevealMenu(entityId, predicate)),
            testid: `fact-reveal-chip-${predicate}`,
          }),
        ],
      ),
      // Status: design-system status-pill atom (per 30-design-system.md
      // §3.3). One show() per status kind so the pill's `data-kind` is
      // always correct without making it a reactive accessor — when
      // the status changes from e.g. draft → hypothetical, the old
      // pill unmounts and the new one mounts with the right palette.
      show(
        state.map((s) => factAtPredicate(s, entityId, predicate)?.status === 'draft'),
        () => [
          statusPill({
            kind: 'draft',
            label: t('fact.status.draft'),
            testid: `fact-status-chip-${predicate}`,
          }),
        ],
      ),
      show(
        state.map((s) => factAtPredicate(s, entityId, predicate)?.status === 'pending'),
        () => [
          statusPill({
            kind: 'pending',
            label: t('fact.status.pending'),
            testid: `fact-status-chip-${predicate}`,
          }),
        ],
      ),
      show(
        state.map((s) => factAtPredicate(s, entityId, predicate)?.status === 'hypothetical'),
        () => [
          statusPill({
            kind: 'hypothetical',
            label: t('fact.status.hypothetical'),
            testid: `fact-status-chip-${predicate}`,
          }),
        ],
      ),
      // Owner=belief: render as a belief-kind status pill (per §3.3
      // belief: italic + secondary palette). Owner chrome and status
      // chrome both fit the status-pill atom; we use the `belief` kind
      // here to surface authorship in the chip stack alongside the
      // primary status (draft / pending / hypothetical).
      show(
        state.map((s) => factAtPredicate(s, entityId, predicate)?.owner.kind === 'belief'),
        () => [
          statusPill({
            kind: 'belief',
            label: t('fact.owner.belief'),
            testid: `fact-owner-chip-${predicate}`,
          }),
        ],
      ),
      // Provenance badge for non-GM authorship (per 30-design-system.md
      // §3.4). GM authorship is the default — no badge — so the chip
      // stack stays uncluttered on the common case. LLM-authored facts
      // pick up the soft purple; player-authored facts the soft green.
      show(
        state.map((s) => authorAtPredicate(s, entityId, predicate) === 'llm'),
        () => [
          provenanceBadge({
            kind: 'llm',
            label: t('fact.author.llm'),
            testid: `fact-provenance-chip-${predicate}`,
          }),
        ],
      ),
      show(
        state.map((s) => authorAtPredicate(s, entityId, predicate) === 'player'),
        () => [
          provenanceBadge({
            kind: 'player',
            label: t('fact.author.player'),
            testid: `fact-provenance-chip-${predicate}`,
          }),
        ],
      ),
      show(
        state.map((s) => authorAtPredicate(s, entityId, predicate) === 'system'),
        () => [
          provenanceBadge({
            kind: 'system',
            label: t('fact.author.system'),
            testid: `fact-provenance-chip-${predicate}`,
          }),
        ],
      ),
      // #251 §14.2 — the GM-only "needs review" marker on an auto-promoted bulk
      // fact (`review_state: 'unreviewed_bulk'`), with a one-click review-pass
      // that clears it. The badge pairs an icon with a label (never colour-only,
      // a11y). GM-only by construction (this whole editor is the GM surface).
      show(
        state.map(
          (s) => factAtPredicate(s, entityId, predicate)?.review_state === 'unreviewed_bulk',
        ),
        () => [
          span(
            {
              class: 'fact-review-flag',
              'data-testid': `fact-review-flag-${predicate}`,
              'data-review-state': 'unreviewed_bulk',
            },
            [
              span({ class: 'fact-review-flag-icon', 'aria-hidden': 'true' }, [text('⚑')]),
              span({ class: 'fact-review-flag-label' }, [
                text(t('bundle.review_state.unreviewed_bulk')),
              ]),
              uiButton({
                variant: 'ghost',
                type: 'button',
                className: 'fact-review-pass',
                testid: `fact-review-pass-${predicate}`,
                ariaLabel: t('bundle.review_pass_label', { predicate: predicateLabel(predicate) }),
                onClick: () => send(host.clearReviewState(entityId, predicate)),
                children: [text(t('bundle.review_pass'))],
              }),
            ],
          ),
        ],
      ),
      // The validity indicator is NOT rendered here as a chip — the
      // inline validity editor (`viewValidityRow`, the `From → Until`
      // line below) is the single validity component now. It states the
      // range in words ("Always"/"Forever" for open-ended bounds), so it
      // needs no separate colour-coded chip / non-color glyph cue.
    ]),
  ];
}

// ────────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────────
// viewSegmentStrip — per-predicate valid-time timeline (ADR-0005 §3/§4).
//
// PROGRESSIVE DISCLOSURE: rendered only for a ∅-uniqueness-key predicate
// (e.g. ruled_by) that ALREADY has more than one valid-time segment. A
// single-segment predicate renders nothing here — it stays exactly today's
// single field. The strip shows the non-overlapping segments as bars with
// INCLUSIVE labels ("…–1066", "1067–"); the boundary STATE is carried by a
// shape/text cue (`data-shape`), never by color alone (WCAG-AA).
//
// "This changed…" is the correct-vs-change FORK: editing the value input in
// place is "Correct" (a `fact.edit`, no new segment, the existing pathway);
// this button issues a `fact.succeed` (close predecessor + open successor)
// using the session cursor as the change day and the freshly-typed input
// value as the new value. The implied predecessor-close is the destructive
// half — surfaced confirmably (title + the dedicated `data-action`).
// ────────────────────────────────────────────────────────────────────

/**
 * Compact display string for a segment's resolved value in the as-of line
 * (#192). Reference / string values render verbatim; numbers stringify;
 * structured (tuple / quantity) values fall back to a stable JSON form. This
 * is a lightweight readout — the row's own value-kind input remains the
 * primary, fully-formatted editor surface.
 */
function formatAsofValue(value: unknown): string {
  if (value === null || value === undefined) return '\u2014';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  const dimensioned = formatNumberValueForDisplay(value);
  if (dimensioned !== null) return dimensioned;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function viewSegmentStrip<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  decl: PredicateDeclaration,
  entityId: string,
  host: FactRowHost<Model, Msg>,
): Renderable {
  const predicate = decl.id;
  // Only ∅-uniqueness-key predicates participate in S1 valid-time succession.
  if (!hasEmptyUniquenessKey(decl)) return [];

  // The strip is reactive over the entity's facts; compute the bar model
  // inside the row-aware `state` signal (already `derived`-wrapped upstream).
  const stripSig = state.map((s) => {
    const entity = entityAtId(s, entityId);
    if (!entity) return { bars: [] as SegmentBar[], single: true, hasOverlap: false };
    const m = buildSegmentStrip(entity, predicate);
    return { bars: m.bars, single: m.single, hasOverlap: m.hasOverlap };
  });

  return [
    show(
      stripSig.map((m) => !m.single),
      () => [
        div(
          {
            class: 'fact-segment-strip',
            'data-testid': `segment-strip-${predicate}`,
            role: 'group',
            'aria-label': t('segments.strip_label'),
          },
          [
            div(
              {
                class: 'segment-strip-bars',
                'data-overlap': stripSig.map((m) => (m.hasOverlap ? 'true' : 'false')),
              },
              [
                each(
                  stripSig.map((m) => m.bars),
                  {
                    key: (bar) => (bar as SegmentBar).key,
                    render: (barSig) => {
                      // Row-aware combine so the calendar label reads the
                      // active calendar off the Model, not the row ctx bag.
                      const labelSig = derived([state, barSig], (s, bar) => {
                        const b = bar as SegmentBar;
                        const calendar = loadActiveCalendar(pickActiveCalendarId(s));
                        const fmt = (day: number): string => {
                          const r = toDisplayDate(calendar, day);
                          return r.ok ? formatDisplayDate(calendar, r.value) : String(day);
                        };
                        return formatSegmentLabel(b, fmt);
                      });
                      return [
                        span(
                          {
                            class: 'segment-bar',
                            'data-testid': 'segment-bar',
                            // NON-COLOR cue: the boundary shape (open-start /
                            // open-end / closed / point) drives the glyph in CSS.
                            'data-shape': barSig.map((b) => (b as SegmentBar).shape),
                            // As-of scrubber (ADR-0005 §3, #192): mark the bar
                            // covering the EFFECTIVE cursor (browse ?? session)
                            // so scrubbing live-highlights the active segment.
                            // Non-color: CSS adds a `▸` glyph, not just a tint.
                            'data-active': derived([state, barSig], (s, bar) => {
                              const entity = entityAtId(s, entityId);
                              if (!entity) return 'false';
                              const day = host.browseCursor(s) ?? host.campaignCursor(s);
                              const seg = resolveBaseSegmentAt(entity.facts, predicate, day);
                              return seg !== null && seg.key === (bar as SegmentBar).key
                                ? 'true'
                                : 'false';
                            }),
                          },
                          [text(labelSig)],
                        ),
                      ];
                    },
                  },
                ),
              ],
            ),
            // As-of resolved value (ADR-0005 §3, #192): the value covering the
            // EFFECTIVE cursor (browse ?? session), live as the GM scrubs. The
            // strip is the timeline's "editor = reader" surface, so this is the
            // entity-page temporal value at `t`.
            div(
              {
                class: 'segment-asof-value',
                'data-testid': `segment-asof-${predicate}`,
                'aria-live': 'polite',
              },
              [
                span({ class: 'segment-asof-label' }, [text(`${t('scrubber.heading')}: `)]),
                span({ class: 'segment-asof-resolved' }, [
                  text(
                    state.map((s) => {
                      const entity = entityAtId(s, entityId);
                      if (!entity) return t('calendar.cursor_unset');
                      const day = host.browseCursor(s) ?? host.campaignCursor(s);
                      const seg = resolveBaseSegmentAt(entity.facts, predicate, day);
                      if (seg === null) return '—';
                      return formatAsofValue(seg.value);
                    }),
                  ),
                ]),
              ],
            ),
            show(
              stripSig.map((m) => m.hasOverlap),
              () => [
                div(
                  { class: 'segment-overlap-warning', role: 'alert', 'data-testid': `segment-overlap-${predicate}` },
                  [text(t('segments.overlap_warning'))],
                ),
              ],
            ),
            uiButton({
              variant: 'secondary',
              type: 'button',
              className: 'segment-succeed-button',
              testid: `segment-succeed-${predicate}`,
              // As-of gate (#192): a succession opens a NEW segment ("new at
              // now"), locked while browsing the past. The reducer also
              // refuses it; this disables the affordance for clarity.
              disabled: state.map((s) => host.browseCursor(s) !== null),
              attrs: {
                'data-action': 'succeed',
                title: t('segments.this_changed_title'),
              },
              onClick: () => {
                  // Event handler — `.peek()` is allowed here. The new value
                  // is whatever the GM typed in the row's input buffer; the
                  // change day is the session cursor (the in-world "now").
                  const model = state.peek();
                  const cursor = host.campaignCursor(model);
                  if (cursor === null) return; // no in-world day → cannot date the change
                  const rowKey = factRowKey(entityId, predicate);
                  const buffered = inputBufferAtKey(model, rowKey);
                  const value = buffered ?? currentValue(factAtPredicate(model, entityId, predicate));
                  if (value === undefined || value === '') return;
                  send(host.succeedFact(entityId, predicate, cursor, value));
                },
              children: [text(t('segments.this_changed'))],
            }),
          ],
        ),
      ],
    ),
  ];
}

// ────────────────────────────────────────────────────────────────────
// viewValidityRow — progressive-disclosure validity editor.
// ────────────────────────────────────────────────────────────────────

function viewValidityRow<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  predicate: string,
  entityId: string,
  host: FactRowHost<Model, Msg>,
): Renderable {
  // The validity editor is shown DIRECTLY now (no disclosure toggle / summary
  // pill). Now that it's a single compact inline line — `From [field] → Until
  // [field]` — there's no value in a collapsed "Timeless/Date" chip; the editor
  // itself communicates the range (or "Always"/"Forever" for open-ended bounds).
  return [
    div({ class: 'fact-validity-row' }, [
      div({ id: `validity-editor-${predicate}`, class: 'validity-editor-host' }, [
        each(
          state.map((s) => {
            const fact = factAtPredicate(s, entityId, predicate);
            const v = fact?.validity;
            // Fold ONLY the open/closed identity of THIS fact's bound picker
            // into the key — so opening or closing the grid remounts the
            // editor (the bound's own gridDateField each does not re-fire its
            // open transition reliably from in here). Deliberately exclude the
            // cursor/overlay: the inner gridDateField each already keys on
            // those, so navigating WITHIN an open grid must NOT remount the
            // outer editor. A mid-interaction outer remount replaces the open
            // popover's buttons between a real click's mousedown and click,
            // dropping the click (#236 — the validity-granularity race).
            const dp = host.datePicker(s);
            const dpKey =
              dp !== null && dp.id.startsWith(`validity-${entityId}-${predicate}-`)
                ? `${dp.id}|open`
                : 'dp0';
            // Key on precision too so a precision-only change remounts the
            // editor (the native <select> reflects the new value).
            if (v === null || v === undefined) return [`none:${dpKey}`] as const;
            return [
              `${v.from ?? 'x'}:${v.to ?? 'x'}:${v.from_precision ?? '-'}:${v.to_precision ?? '-'}:${dpKey}`,
            ] as const;
          }),
          {
            key: (k) => k,
            render: () => {
              const model = state.peek();
              const fact = factAtPredicate(model, entityId, predicate);
              return [
                validityEditor({
                  calendar: host.activeCalendar(model),
                  value: fact?.validity ?? null,
                  // Un-open-ending a bound seeds the current Campaign
                  // date (the in-world time the GM is working in), not
                  // the calendar epoch.
                  defaultSeed: host.campaignCursor(model),
                  id: `validity-${entityId}-${predicate}`,
                  testid: `validity-editor-${predicate}`,
                  messages: {
                    era: t('calendar.era'),
                    year: t('calendar.year'),
                    month: t('calendar.month'),
                    day: t('calendar.day'),
                    clear: t('validity.set_open_ended'),
                    unset: t('validity.open_ended'),
                    fromLabel: t('validity.from'),
                    toLabel: t('validity.to'),
                    heading: t('validity.heading'),
                    // Per-bound copy — "Set to open-ended (no start
                    // date)" on the from chip, "(no end date)" on the
                    // to chip; same for the post-clear placeholder.
                    // The validity-editor's chip picks the right one
                    // by bound and falls back to the legacy
                    // setOpenEnded/openEnded fields if these are
                    // unset.
                    openEndedFrom: t('validity.open_ended_from'),
                    openEndedTo: t('validity.open_ended_to'),
                    setOpenEndedFrom: t('validity.set_open_ended_from'),
                    setOpenEndedTo: t('validity.set_open_ended_to'),
                    setDateBound: t('validity.set_a_date'),
                    fromPrecisionLabel: t('validity.from_precision'),
                    toPrecisionLabel: t('validity.to_precision'),
                    precisionOptions: {
                      day: t('validity.precision.day'),
                      month: t('validity.precision.month'),
                      season: t('validity.precision.season'),
                      year: t('validity.precision.year'),
                    },
                    noneFromLabel: t('validity.none_from'),
                    noneToLabel: t('validity.none_to'),
                  },
                  // Inject the calendar-grid dateField for each bound — a
                  // single inline control: it shows the date or the
                  // open-ended label (`placeholder`), and its folded-in
                  // picker carries precision + the "none" clear.
                  renderPicker: ({
                    value,
                    onChange,
                    id,
                    testid,
                    placeholder,
                    granularity,
                    granularities,
                    onGranularity,
                    granularityClearLabel,
                    onGranularityClear,
                  }) =>
                    gridDateField(state, send, host, {
                      id,
                      testid: testid ?? id,
                      clearable: false,
                      valueOf: () => value,
                      onSelect: (day) => onChange(day),
                      placeholder,
                      granularity,
                      granularities,
                      onGranularity,
                      granularityClearLabel,
                      onGranularityClear,
                    }),
                  onChange: (next) =>
                    send(host.setFactValidity(entityId, predicate, next)),
                }),
              ];
            },
          },
        ),
      ]),
    ]),
  ];
}

// ────────────────────────────────────────────────────────────────────
// viewHistory — per-op rollback panel (IV.E.1 / #87).
// ────────────────────────────────────────────────────────────────────

function viewHistory<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  predicate: string,
  entityId: string,
  host: FactRowHost<Model, Msg>,
): Mountable[] {
  return [uiButton({
    variant: 'ghost',
    className: 'fact-history-trigger',
    testid: `history-${predicate}`,
    attrs: { 'aria-haspopup': 'dialog', title: t('entity.history') },
    // The row re-renders when its input loses focus, so without this a click
    // straight after typing blurs the input and tears this button down before
    // the click lands — History looked inert. See `ButtonOptions.preventBlur`.
    preventBlur: true,
    onClick: () => send(host.openHistory(entityId, predicate)),
    children: [
      uiIcon('history', { size: 14 }),
      span({ class: 'fact-history-trigger-label' }, [text(t('entity.history'))]),
    ],
  })];
}

/**
 * The History dialog body — the version list for the open (entity, predicate).
 *
 * Rendered top-level by the consumer (like the trait picker) so it overlays
 * every surface instead of expanding the row it belongs to.
 */
export function viewHistoryDialog<Model extends FactRowStateShape, Msg>(
  state: Signal<Model>,
  send: Send<Msg>,
  host: FactRowHost<Model, Msg>,
): Mountable {
  return modal({
    open: state.map((s) => s.historyOpen !== null),
    onDismiss: () => send(host.closeHistory()),
    labels: { close: t('mobile.bottom_sheet.close') },
    labelId: 'fact-history-heading',
    testid: 'fact-history-dialog',
    panelClass: 'fact-history-panel',
    size: 'md',
    children: [
      h2({ id: 'fact-history-heading', class: 'fact-history-heading' }, [
        text(t('entity.history_heading')),
      ]),
      ol({ class: 'fact-history-list' }, [
      each(
        state.map((s) => {
          const open = s.historyOpen;
          if (open === null) return [];
          const fact = factAtPredicate(s, open.entityId, open.predicate);
          if (!fact) return [];
          // The owner label only earns a badge when it is NOT plain canon —
          // a `[canon]` chip on every row of a canon fact is pure noise (it
          // repeated the status word already in the meta line).
          const beliefLabel =
            fact.owner.kind === 'belief' ? `${t('entity.history.belief_label')}: ${fact.owner.viewer.kind}` : '';
          // Reverse-chronological order (newest first). The original
          // `idx` is preserved on each row because the rollback Msg
          // references positions in `fact.versions`, not display
          // positions.
          return fact.versions
            .map((v, idx) => ({
              idx,
              author: v.author,
              // Absolute, locale-aware instant. The raw ISO string used to
              // render verbatim ("2026-06-20T19:31:45.496Z"), which is
              // unreadable at a glance and not i18n-safe.
              writtenAt: formatHistoryInstant(v.written_at_real),
              valueDisplay: String(v.value),
              beliefLabel,
              isLatest: idx === fact.versions.length - 1,
            }))
            .reverse();
        }),
        {
          key: (entry) => String(entry.idx),
          render: (item) => [
            li(
              {
                class: 'fact-history-entry',
                'data-testid': 'history-entry',
                // NON-COLOR cue: the current version is marked with a badge
                // AND this attribute drives a leading rule in CSS.
                'data-current': item.at('isLatest').map((latest) => String(latest)),
              },
              [
                div({ class: 'fact-history-entry-main' }, [
                  span({ class: 'fact-history-value' }, [text(item.at('valueDisplay'))]),
                  show(item.at('isLatest'), () => [
                    badge({
                      tone: 'neutral',
                      attrs: { 'data-kind': 'current' },
                      children: [text(t('entity.history.latest'))],
                    }),
                  ]),
                  show(
                    item.at('beliefLabel').map((label) => label !== ''),
                    () => [
                      badge({
                        tone: 'neutral',
                        attrs: { 'data-kind': 'owner' },
                        children: [text(item.at('beliefLabel'))],
                      }),
                    ],
                  ),
                ]),
                div({ class: 'fact-history-meta' }, [
                  span({ class: 'fact-history-author' }, [text(item.at('author'))]),
                  span({ class: 'fact-history-sep', 'aria-hidden': 'true' }, [text('·')]),
                  span({ class: 'fact-history-when' }, [text(item.at('writtenAt'))]),
                ]),
                show(
                  item.at('isLatest').map((latest) => !latest),
                  () => [
                    uiButton({
                      variant: 'ghost',
                      testid: 'history-rollback',
                      className: 'fact-history-rollback',
                      onClick: () => {
                        // See list-view.ts:240 — `item.current()` is
                        // broken under llui v0.5.7. Use field accessors.
                        const idx = item.at('idx').peek();
                        if (typeof idx !== 'number') return;
                        const open = state.peek().historyOpen;
                        if (open === null) return;
                        send(host.rollbackFact(open.entityId, open.predicate, idx));
                        send(host.closeHistory());
                      },
                      children: [text(t('entity.rollback'))],
                    }),
                  ],
                ),
              ],
            ),
          ],
        },
      ),
    ]),
    ],
  });
}

/**
 * Format an op-log write instant for the History panel: absolute and
 * locale-aware. Absolute (not relative) because a version log answers "when
 * did this happen"; `Intl.DateTimeFormat` keeps it i18n/RTL-safe. Falls back
 * to the raw ISO string on a parse failure rather than throwing.
 */
function formatHistoryInstant(iso: string): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const locale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(ms);
  } catch {
    return new Intl.DateTimeFormat('en', { dateStyle: 'medium', timeStyle: 'short' }).format(ms);
  }
}
